import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  Badge, Checkbox, Collapse, DatePicker,
  Descriptions, Drawer, Empty, Input,
  Modal, Radio, Select, Slider, Spin,
  Tag, Timeline, message, Tooltip, Tabs
} from 'antd'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { LeftOutlined, RightOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons'
import Button from 'components/Button/index'
import AntButton from 'antd/es/button'
import { StyledTable } from 'components/TableStyles/TableStyles'
import TableSearchFilter from 'components/UIComponents/TableSearchFilter'
import Pagination from 'components/Pagination'
import {
  EmptyWrapper,
  NavWrapper,
  SectionWrapper,
  TopHeader,
  TopHeading,
} from 'modules/Corporates/Style/style'
import {
  RowNumberData,
  RowNumberEmailData,
} from 'components/TableStyles/TableContentStyles/TableContentStyles'
import candidateRequest from 'utils/candidateRequest'
import ExportStatusPanel from 'components/ExportStatusPanel'
import { analyticsSession, resetSessionIfLeavingAnalytics } from 'utils/analyticsSession'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Pie } from '@ant-design/plots'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import moment from 'moment'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import elasticSearchRequest from 'utils/elasticSearchRequest'
dayjs.extend(customParseFormat)

const { Panel } = Collapse
const PAGE_SIZE = 10

// Options for the paged, server-searched multi-selects.
//
// rc-select toggles a multi-select row by its `value`, and rc-virtual-list
// reconciles rows by `key`. A duplicate value therefore breaks BOTH: the row
// cannot be unchecked (two rows claim the same value) and the list jumps while
// scrolling (React falls back to index keys). Paged option lists must stay
// unique by value and carry a stable key.
const toOption = (value) => ({ value, label: value, key: value })

const mergeOptionsByValue = (prev, next) => {
  const seen = new Set()
  return [...prev, ...next].filter((option) => {
    if (seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}


const NormalizedCard = ({ item }) => {
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        background: '#ffffff',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
        {item.key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase())
        }
      </div>

      <div
        style={{
          fontSize:
            (item.label || item.key || '')
              .toLowerCase()
              .includes('email')
              ? 12
              : 14,
          fontWeight: 500,
          color: '#111827',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {item.value || '-'}
      </div>
    </div>
  )
}

const NormalizedGrid = ({ data }) => {
  if (!data || !data.length) {
    return <Empty description="No normalized data" />
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 12,
      }}
    >
      {data.map((item, index) => (
        <NormalizedCard key={index} item={item} />
      ))}
    </div>
  )
}



// ─── Helpers ──────────────────────────────────────────────────────────────────
const smartFormat = (val) => {
  if (val === null || val === undefined || val === '') return '-'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (Array.isArray(val)) {
    if (!val.length) return '-'
    return (
      <>
        {val.map((item, i) => {
          if (typeof item === 'string' || typeof item === 'number')
            return <Tag key={i}>{item}</Tag>
          if (typeof item === 'object') {
            if (item.city_name || item.state_name)
              return <Tag key={i}>{[item.city_name, item.state_name].filter(Boolean).join(', ')}</Tag>
            return null
          }
          return <Tag key={i}>{String(item)}</Tag>
        })}
      </>
    )
  }
  if (typeof val === 'object') return JSON.stringify(val, null, 2)
  return String(val)
}

const fmtDate = (val) => {
  if (!val) return '-'
  if (typeof val === 'number') return new Date(val).toLocaleDateString('en-IN')
  // ISO string (YYYY-MM-DD...) -> DD/MM/YYYY; anything else passes through
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(val).split('T')[0]
}

// ─── InfoSection ──────────────────────────────────────────────────────────────
const InfoSection = ({ title, fields, data, column = 2 }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <Descriptions
      title={<span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{title}</span>}
      bordered size="small" column={column}
      labelStyle={{ fontWeight: 500, background: '#f9fafb', width: '30%' }}
      contentStyle={{ background: '#fff' }}
    >
      {fields.map(({ label, key, render }) => (
        <Descriptions.Item key={key} label={label}>
          {render ? render(data) : smartFormat(data?.[key])}
        </Descriptions.Item>
      ))}
    </Descriptions>
  </div>
)

// ─── EducationHistory ─────────────────────────────────────────────────────────
const EducationHistory = ({ education }) => {
  if (!education?.length)
    return <span style={{ color: '#9ca3af' }}>No education records</span>
  const levelOrder = { tenth: 1, twelfth: 2, diploma: 3, ug: 4, pg: 5 }
  const sorted = [...education].sort(
    (a, b) => (levelOrder[a.education_level] || 99) - (levelOrder[b.education_level] || 99)
  )
  return (
    <Timeline>
      {sorted.map((edu, i) => (
        <Timeline.Item key={i}>
          <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', fontSize: 12, color: '#6b7280' }}>
              {edu.education_level || '-'}
            </div>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Institution">{smartFormat(edu?.institution_name)}</Descriptions.Item>
              <Descriptions.Item label="Board">{smartFormat(edu?.board)}</Descriptions.Item>
              <Descriptions.Item label="Marks">{edu?.marks != null ? String(edu.marks) : '-'}</Descriptions.Item>
              <Descriptions.Item label="Started">{fmtDate(edu?.started_on)}</Descriptions.Item>
              <Descriptions.Item label="Ended">{fmtDate(edu?.ended_on)}</Descriptions.Item>
            </Descriptions>
          </div>
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

// ─── Field configs ────────────────────────────────────────────────────────────
const STUDENT_FIELDS = [
  // { label: 'Uni Roll No',        key: 'uniRollNo' },
  // { label: 'Institute Campus ID',  key: 'instituteCampusId' },
  { label: 'Institute', key: 'instituteCampusName' },
  { label: 'Course ID', key: 'courseId' },
  { label: 'Exp Candidate', key: 'isExpCandidate' },
  {
    label: 'CV URL', key: 'cvUrl', render: (d) => {
      const cv = d?.cvUrl
      if (!cv) return '-'
      if (typeof cv === 'object' && cv.url)
        return <a href={cv.url} target="_blank" rel="noreferrer">{cv.name || 'View CV'}</a>
      return '-'
    }
  },
  {
    label: 'CV Details', key: 'cvDetails',
    render: (d) => {
      const cvd = d?.cvDetails
      if (!cvd || typeof cvd !== 'object') return '-'

      const renderValue = (val) => {
        if (val === null || val === undefined || val === '' || val === 0) return null
        if (typeof val === 'boolean') return val ? 'Yes' : 'No'
        if (typeof val === 'string') return val.trim() || null
        if (typeof val === 'number') return String(val)

        // Array
        if (Array.isArray(val)) {
          const filtered = val.filter(item => {
            if (!item) return false
            if (typeof item === 'object') return Object.values(item).some(v => v !== '' && v !== null && v !== undefined && v !== 0)
            return true
          })
          if (!filtered.length) return null
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filtered.map((item, i) => (
                <div key={i} style={{ paddingLeft: 8, borderLeft: '2px solid #e5e7eb' }}>
                  {typeof item === 'object' ? renderObject(item) : String(item)}
                </div>
              ))}
            </div>
          )
        }

        // Object
        if (typeof val === 'object') return renderObject(val)

        return String(val)
      }

      const renderObject = (obj) => {
        const entries = Object.entries(obj).filter(([, v]) => {
          if (v === null || v === undefined || v === '' || v === 0) return false
          if (Array.isArray(v)) return v.length > 0
          if (typeof v === 'object') return Object.values(v).some(x => x !== '' && x !== null && x !== undefined)
          return true
        })
        if (!entries.length) return null
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {entries.map(([key, val]) => {
              const rendered = renderValue(val)
              if (rendered === null) return null
              return (
                <div key={key} style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                  <span style={{ color: '#9ca3af', minWidth: 110, textTransform: 'capitalize', flexShrink: 0 }}>
                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </span>
                  <span style={{ color: '#1f2937' }}>{rendered}</span>
                </div>
              )
            })}
          </div>
        )
      }

      const topLevelEntries = Object.entries(cvd).filter(([, v]) => v !== null && v !== undefined && v !== '')
      if (!topLevelEntries.length) return '-'

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {topLevelEntries.map(([key, val]) => {
            const rendered = renderValue(val)
            if (rendered === null) return null
            return (
              <div key={key}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                  {key.replace(/([A-Z])/g, ' $1')}
                </div>
                <div style={{ paddingLeft: 8 }}>{rendered}</div>
              </div>
            )
          })}
        </div>
      )
    },
  },
]

const PERSONAL_FIELDS = [
  // { label: 'Father',    key: 'father' },
  // { label: 'Mother',    key: 'mother' },
  { label: 'Date of Birth', key: 'dob', render: (d) => fmtDate(d?.dob) },
  { label: 'Mobile Number', key: 'contact_number' },
  { label: 'Gender', key: 'gender' },
  { label: 'Languages', key: 'languages', render: (d) => smartFormat(d?.languages) },
  { label: 'Preferred Location', key: 'preferredJobLocation' },
  { label: 'Address', key: 'permAddrLine1' },
  { label: 'City', key: 'permCity' },
  { label: 'State', key: 'permState' },
  { label: 'Country', key: 'permCountry' },
  { label: 'Post Code', key: 'permPostCode' },
]

const CURRENT_COURSE_FIELDS = [
  { label: 'Education Level', key: 'education_level' },
  { label: 'Degree', key: 'degree' },
  { label: 'Department', key: 'department' },
  {
    label: 'Average Marks', key: 'average_marks', render: (d) =>
      d?.average_marks != null
        ? `${d.average_marks}${d.cumulative_type ? ` (${d.cumulative_type})` : ''}`
        : '-',
  },
  { label: 'Started On', key: 'started_on', render: (d) => fmtDate(d?.started_on) },
  { label: 'Ended On', key: 'ended_on', render: (d) => fmtDate(d?.ended_on) },
]

const parseUrlParams = (searchParams) => {
  const filters = { ...DEFAULT_FILTERS }
  let searchValue = ''
  const gender = searchParams.get('gender')
  const status = searchParams.get('status')
  const passed_out = searchParams.get('passed_out')
  const profile_min = searchParams.get('profile_min')
  const profile_max = searchParams.get('profile_max')
  const institute = searchParams.get('institute_campus')
  const institute_campus_id = searchParams.get('institute_campus_id')
  const education_level = searchParams.get('education_level')
  const degree = searchParams.get('degree')
  const department = searchParams.get('department')
  const city = searchParams.get('city')
  const state = searchParams.get('state')
  const is_exp = searchParams.get('is_exp_candidate')
  const roles = searchParams.get('roles')
  const search = searchParams.get('search')
  const work_corporate_ids = searchParams.get('work_corporate_ids') || searchParams.get('work_corporate')
  const work_company = searchParams.get('work_company')

  if (gender) filters.gender = gender
  if (status !== null && status !== '') filters.status = parseInt(status, 10)
  if (passed_out === 'true') filters.passed_out = true
  if (passed_out === 'false') filters.passed_out = false
  if (is_exp === 'true') filters.is_exp_candidate = true
  if (is_exp === 'false') filters.is_exp_candidate = false
  if (institute) filters.institute_campus = [institute]
  if (institute_campus_id) filters.institute_campus_ids = [institute_campus_id]
  if (education_level) filters.education_level = education_level
  if (degree) filters.degree = [degree]
  if (department) filters.department = [department]
  if (city) filters.city = [city]
  if (state) filters.state = [state]
  if (roles) filters.roles = [roles]
  if (search) searchValue = search
  if (work_corporate_ids) filters.work_corporate_ids = work_corporate_ids.split('||')
  if (work_company) filters.work_company = work_company.split('||')
  const pMin = profile_min !== null && profile_min !== '' ? parseInt(profile_min, 10) : 0
  const pMax = profile_max !== null && profile_max !== '' ? parseInt(profile_max, 10) : 100
  if (pMin !== 0 || pMax !== 100) filters.profile = [pMin, pMax]
  return { filters, searchValue }
}

// Statuses the export worker never moves out of, so polling can stop.
// 'expired' is terminal too: the retention sweep has released the file bytes.
// 'done' is deliberately absent — delivery is by email, so a file that is built
// but not yet emailed is still in flight as far as the requester is concerned.
// Polling stops on `notified`, handled alongside this list.
const TERMINAL_EXPORT_STATUSES = ['failed', 'expired']

// The API already de-duplicates role titles, but the picker can receive an
// older page after a newer search/open request. Keep the UI idempotent as well:
// labels that only differ by case or whitespace are one option.
const mergeRoleOptions = (options) => {
  const seen = new Set()
  return options.filter((option) => {
    const key = String(option?.value ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase()
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// ─── Default filters ──────────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  gender: null,
  status: null,
  passed_out: null,
  profile: [0, 100],
  institute_campus: [],
  institute_campus_id: null,
  institute_campus_ids: [],
  education_level: null,
  degree: [],
  department: [],
  city: [],
  state: [],
  is_exp_candidate: null,
  roles: [],
  roleSearch: '',
  work_company: [],
  work_designation: [],
  work_industry: [],
  work_location: '',
  work_started_from: null,
  work_started_to: null,
  notice_period: null,
  work_corporate_ids: [],
  salary_min: '',
  salary_max: '',
  passing_years: [],
}

// shorthand aliases into the shared analytics session
const _saved = () => analyticsSession.candidates

// ─── Main Component ───────────────────────────────────────────────────────────
const CandidateMetricMain = () => {
  const currentUserId = useSelector(state => state.auth?.userId) || null
  const [assessmentData, setAssessmentData] = useState(null)
  const [assessmentLoading, setAssessmentLoading] = useState(false)

  const [exportStatus, setExportStatus] = useState(null) // shape: { jobId, status, fileFormat, requestedAt } or null
  const pollRef = useRef(null)

  // Delivery is by email, always. A full export takes 5-6 minutes to build and
  // runs to ~90 MB, so the requester is not kept on the page waiting for a
  // download — the worker emails them when it is ready.
  const currentUserEmail = useSelector(state => state.auth?.userData?.email) || ''

  const [selectedRow, setSelectedRow] = useState(null)

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current)  // clear any old poll
    // let attempts = 0
    pollRef.current = setInterval(async () => {
      // attempts++
      // if (attempts > 30) {
      //   clearInterval(pollRef.current)
      //   setExportStatus(prev => ({ ...prev, status: 'failed', errorMessage: 'Export timed out. Please try again.' }))
      //   return
      // }
      try {
        const resp = await candidateRequest.get(`/api/student-metrics/download/status/${jobId}`)
        const { status, error, notified, notify_email: notifyEmail } = resp.data
        setExportStatus(prev =>
          prev
            ? { ...prev, status, notified, notifyEmail, errorMessage: error || null }
            : prev
        )
        if (TERMINAL_EXPORT_STATUSES.includes(status) || notified) {
          clearInterval(pollRef.current)
        }
      } catch { /* keep polling */ }
    }, 4000)
  }

  // ADD this useEffect — runs once on mount
  useEffect(() => {
    const checkPendingExport = async () => {
      try {
        const resp = await candidateRequest.get('/api/student-metrics/download/pending', {
          params: { user_id: currentUserId },
        })
        console.log('pending job check:', resp?.data)
        const job = resp?.data?.job
        if (job) {
          setExportStatus({
            jobId: job.job_id,
            status: job.status,           // 'pending' | 'processing' | 'done'
            fileFormat: job.file_format,
            requestedAt: job.created_at,  // ISO string from DB
            notified: false,
            notifyEmail: null,
            errorMessage: job.error_message || null,
          })
          if (!TERMINAL_EXPORT_STATUSES.includes(job.status)) {
            // Even a 'done' job is resumed: the status poll is what reports
            // whether the email has gone out yet.
            startPolling(job.job_id)
          }
        }
      } catch (e) {
        console.error('pending check failed:', e)
      }
    }
    checkPendingExport()
  }, [])

  // Fetch downloadable columns from normalized_columns on mount
  useEffect(() => {
    const fetchDownloadCols = async () => {
      setColsLoading(true)
      try {
        const resp = await candidateRequest.get('/api/student-metrics/live-candidates-download-columns')
        const cols = (resp?.data?.data || []).map(r => ({
          slug_name: r.slug_name,
          display_name: r.display_name,
        }))
        setAllDownloadCols(cols)
        setAvailableCols(cols)
      } catch (e) {
        console.error('Failed to fetch download columns:', e)
      } finally {
        setColsLoading(false)
      }
    }
    fetchDownloadCols()
  }, [])


  const fetchAssessmentData = async (studentId) => {
    if (!studentId) return

    setAssessmentLoading(true)

    try {
      const resp = await candidateRequest.get(
        `/api/student-metrics/assessment/${studentId}`
      )

      const data = resp?.data?.data || {}

      // normalize response safely
      setAssessmentData({
        aptitude_score: parseFloat(data?.aptitude_score ?? data?.aptitude ?? 0),
        behavior_score: parseFloat(data?.behavior_score ?? data?.behavior ?? 0),
        communication_score: parseFloat(data?.communication_score ?? data?.communication ?? 0),
      })
    } catch (err) {
      message.error('Failed to load assessment data')
      setAssessmentData(null)
    } finally {
      setAssessmentLoading(false)
    }
  }


  const ChartContainer = ({ children }) => (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
      }}
    >
      {children}
    </div>
  )

  const PieChartWrapper = ({ children }) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      {children}
    </div>
  )

  const NoData = ({ children }) => (
    <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
      {children}
    </div>
  )

  const renderAssessmentScores = () => {
    const aptitude = Number(assessmentData?.aptitude_score || 0)
    const behavior = Number(assessmentData?.behavior_score || 0)
    const communication = Number(assessmentData?.communication_score || 0)
    if (!aptitude && !behavior && !communication) {
      return <NoData>No assessment scores available</NoData>
    }

    const pieData = [
      { type: 'Aptitude', value: aptitude },
      { type: 'Behavior', value: behavior },
      { type: 'Communication', value: communication }
    ].filter(item => item.value > 0)

    const config = {
      data: pieData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.9,
      innerRadius: 0.65,

      label: {
        text: (d) => `${d.type}\n${d.value.toFixed(1)}`,
        position: 'spider',
        style: {
          fontSize: 12,
          fontWeight: '600',
          fill: '#6b7280'
        }
      },

      legend: false,

      tooltip: {
        title: 'type',
        items: [
          {
            field: 'value',
            name: 'Score',
            valueFormatter: (v) => Number(v).toFixed(2)
          }
        ]
      },

      annotations: [
        {
          type: 'text',
          style: {
            text: 'Assessment\nScores',
            x: '50%',
            y: '50%',
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 'bold',
            fill: '#1f2937'
          }
        }
      ],

      scale: {
        color: {
          range: ['#667eea', '#764ba2', '#f59e0b']
        }
      },

      style: {
        stroke: '#fff',
        lineWidth: 2
      }
    }

    return (
      <ChartContainer>
        <PieChartWrapper>
          <Pie {...config} height={280} />
        </PieChartWrapper>
      </ChartContainer>
    )
  }

  const renderLiveDataAnalysis = () => {
    if (!s) return <NoData>No candidate data available</NoData>

    // Prepare data for charts
    const educationData = s.education?.map(edu => ({
      level: edu.education_level || 'Unknown',
      marks: parseFloat(edu.marks) || 0,
      institution: edu.institution_name || 'Unknown',
      board: edu.board || 'N/A',
      startedOn: edu.started_on || 'N/A',
      endedOn: edu.ended_on || 'N/A'
    })) || []

    const levelOrder = {
      tenth: 1,
      twelfth: 2,
      diploma: 3,
      ug: 4,
      pg: 5
    }

    const sortedEducationData = [...educationData].sort((a, b) => {
      const aLevel = (a.level || '').toLowerCase()
      const bLevel = (b.level || '').toLowerCase()

      return (levelOrder[aLevel] || 999) - (levelOrder[bLevel] || 999)
    })

    const profileScore = s.student?.profileScore || 0

    // Current Course Data
    const currentCourseData = {
      education_level: s.currentCourse?.education_level || 'N/A',
      degree: s.currentCourse?.degree || 'N/A',
      institution: s.currentCourse?.institution_name || 'N/A',
      averageMarks: s.currentCourse?.averageMarks || 'N/A',
      startedOn: s.currentCourse?.startedOn || 'N/A',
      endedOn: s.currentCourse?.endedOn || 'N/A'
    }
    // Profile Card Component
    const ProfileCard = () => (
      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: 16,
          padding: 24,
          color: '#111827',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          marginBottom: 24
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* Avatar */}
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(165, 134, 167, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 'bold',
              backdropFilter: 'blur(10px)'
            }}
          >
            {s.admin?.firstName?.charAt(0)?.toUpperCase() || 'S'}
          </div>

          {/* Profile Info */}
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
              {s.admin?.firstName ? `${s.admin.firstName} ${s.admin.lastName || ''}`.trim() : 'Student'}
            </h2>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {s.admin?.email || 'No email'}
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, opacity: 0.8 }}>
                📍 {s.studentPersonalProfile?.corrCity || 'N/A'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.8 }}>
                📞 {s.studentPersonalProfile?.contact_number || 'N/A'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.8 }}>
                👤 {s.studentPersonalProfile?.gender || 'N/A'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.8 }}>
                🎂 {fmtDate(s.studentPersonalProfile?.dob) || 'N/A'}
              </span>
            </div>

          </div>

          {/* CV Download Button - Only show if CV is available */}
          {s.student?.cvUrl &&
            s.student.cvUrl !== '' &&
            s.student.cvUrl !== null && (
              <div>
                {(() => {
                  const cv = s.student.cvUrl
                  const cvUrl = typeof cv === 'object' ? cv?.url : cv
                  const hasCvUrl =
                    cvUrl &&
                    typeof cvUrl === 'string' &&
                    cvUrl.trim() !== '' &&
                    cvUrl !== 'null' &&
                    cvUrl !== 'undefined'
                  return hasCvUrl ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span
                        style={{
                          color: '#111827',
                          fontWeight: 600,
                          fontSize: 14
                        }}
                      >
                        Resume
                      </span>
                      <a
                        href={cvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: '#3B82F6',
                          fontWeight: 600,
                          fontSize: 14,
                          cursor: 'pointer',
                          borderBottom: '1px solid transparent',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderBottom = '1px solid #3B82F6'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderBottom = '1px solid transparent'
                        }}
                      >

                        <DownloadOutlined
                          style={{
                            fontSize: 20
                          }}
                        />

                      </a>
                    </div>
                  ) : null
                })()}
              </div>
            )}
        </div>

      </div>

    )

    // Enhanced Education Chart with Hover Effects
    const EnhancedEducationChart = () => {


      const levelColors = {
        tenth: '#3B82F6',
        twelfth: '#8B5CF6',
        ug: '#10B981',
        pg: '#F59E0B'
      }

      const formattedEducationData = educationData.map((edu) => ({
        level: edu.level || '-',
        marks: Number(edu.marks ?? 0),
        institution:
          edu.institution &&
            edu.institution.trim() !== '' &&
            edu.institution !== 'Unknown' &&
            edu.institution !== ''
            ? edu.institution
            : '',
        board: edu.board || '-',
        startedOn: edu.startedOn ? fmtDate(edu.startedOn) : '-',
        endedOn: edu.endedOn ? fmtDate(edu.endedOn) : '-'
      }))

      return (
        <ChartContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 20
            }}
          >
            <h4 style={{ fontSize: 18, fontWeight: 700 }}>
              🎓 Education Journey
            </h4>
          </div>

          {/* CUSTOM CHART (NO LIB LIMITATIONS) */}
          {!formattedEducationData.length ? (

            <div
              style={{
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 16,
                padding: '60px 20px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              No education data available
            </div>

          ) : (

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {formattedEducationData.map((item, index) => {
                const color =
                  levelColors[item.level?.toLowerCase()] || '#3249D7'

                return (
                  <div
                    key={index}
                    style={{
                      background: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: 16,
                      padding: 16,
                      boxShadow: '0 3px 10px rgba(0,0,0,0.04)'
                    }}
                  >

                    {/* TOP ROW */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            color: '#111827'
                          }}
                        >
                          {item.level}
                        </div>
                        {item.institution && (
                          <div style={{ fontSize: 12, color: '#6B7280' }}>
                            🏫 {item.institution}
                          </div>
                        )}
                      </div>

                      {/* MARKS BADGE */}
                      <div
                        style={{
                          background: color,
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontWeight: 700,
                          fontSize: 13
                        }}
                      >
                        {item.marks}%
                      </div>
                    </div>

                    {/* PROGRESS BAR */}
                    <div
                      style={{
                        height: 14,
                        width: '100%',
                        background: '#E5E7EB',
                        borderRadius: 999,
                        overflow: 'hidden',
                        marginBottom: 10
                      }}
                    >
                      <div
                        style={{
                          width: `${item.marks}%`,
                          height: '100%',
                          background: color,
                          borderRadius: 999,
                          transition: 'width 0.6s ease'
                        }}
                      />
                    </div>

                    {/* BOTTOM INFO */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 12,
                        color: '#6B7280'
                      }}
                    >
                      <span>🏫 {item.board}</span>
                      <span>
                        📅 {item.startedOn} → {item.endedOn}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ChartContainer>
      )
    }
    // Simple Roles Display
    const RolesDisplay = () => {

      // Use actual roles if available, otherwise demo roles
      const roles = s.roles || []

      return (
        <ChartContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16
            }}
          >
            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                margin: 0,
                color: '#1f2937'
              }}
            >
              💼 Roles Applied
            </h4>

            <div
              style={{
                background: '#10b981',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {roles.length} Roles
            </div>
          </div>

          {roles.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,

                // Scroll styles
                maxHeight: '420px',
                overflowY: 'auto',
                paddingRight: 6
              }}
            >
              {roles.map((role, index) => (
                <div
                  key={index}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0'
                    e.currentTarget.style.transform = 'translateX(4px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f8fafc'
                    e.currentTarget.style.transform = 'translateX(0)'
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#10b981',
                      flexShrink: 0
                    }}
                  />

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#1f2937'
                    }}
                  >
                    {role}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#9ca3af'
              }}
            >
              No roles applied yet
            </div>
          )}
        </ChartContainer>
      )
    }
    // Key metrics cards
    const metricsCards = [

      {
        title: 'Highest Education Level',
        value: s.currentCourse?.education_level || 'N/A',
        subtitle: s.currentCourse?.degree || 'No degree',
        color: '#10b981',
        icon: '🎓'
      },
      {
        title: 'Total Roles',
        value: s.roles?.length || 0,
        subtitle: 'Job applications',
        color: '#f59e0b',
        icon: '💼'
      },
      {
        title: 'Experience',
        value: s.student?.isExpCandidate ? 'Experienced' : 'Fresher',
        subtitle: 'Career level',
        color: s.student?.isExpCandidate ? '#8b5cf6' : '#6b7280',
        icon: '⭐'
      }
    ]

    const MetricCard = ({ title, value, subtitle, color, icon, trend }) => (
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)'
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)'
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        {/* Trend indicator */}
        {trend && (
          <div style={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontSize: 16,
            opacity: 0.6
          }}>
            {trend === 'up' ? '📈' : trend === 'down' ? '📉' : '➡️'}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 28, marginRight: 12 }}>{icon}</div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{title}</div>
        </div>
        <div style={{ fontSize: 24, fontWeight: 'bold', color: color, marginBottom: 4 }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{subtitle}</div>
      </div>
    )

    const aptitude = Number(
      assessmentData?.aptitude_score || 0
    )

    const behavior = Number(
      assessmentData?.behavior_score || 0
    )

    const communication = Number(
      assessmentData?.communication_score || 0
    )

    const hasAssessmentData =
      aptitude > 0 ||
      behavior > 0 ||
      communication > 0

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Profile Section */}
        <ProfileCard />

        {/* Key Metrics Cards */}
        <div
          style={{
            padding: 18,
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 16,
            }}
          >

            {/* Current Course Card */}
            <div
              style={{
                padding: 16,
                borderRadius: 12,
                background: '#F9FAFB',
                border: '1px solid #EEF2F7',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 10,
                }}
              >
                Current Course
              </div>

              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 6,
                }}
              >
                {s.currentCourse?.department || 'N/A'}
              </div>

              <div
                style={{
                  fontSize: 15,
                  color: '#4B5563',
                  marginBottom: 14,
                  lineHeight: 2.0,
                }}
              >
                {s.currentCourse?.degree || 'N/A'} •{' '}
                {s.currentCourse?.education_level || 'N/A'}
                <br />
                {s.student.instituteCampusName || 'N/A'}
              </div>

              {/* Average Marks */}
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: '#6B7280',
                      fontWeight: 500,
                    }}
                  >
                    Average Marks Scored
                  </span>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#111827',
                    }}
                  >
                    {s.currentCourse?.average_marks || 'N/A'}%
                  </span>
                </div>

                <div
                  style={{
                    height: 8,
                    width: '100%',
                    background: '#E5E7EB',
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${s.currentCourse?.average_marks || 0}%`,
                      height: '100%',
                      borderRadius: 999,
                      background:
                        (s.currentCourse?.average_marks || 0) >= 75
                          ? '#10B981'
                          : (s.currentCourse?.average_marks || 0) >= 50
                            ? '#6366F1'
                            : '#F59E0B',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1f2937' }}>
            📈 Key Metrics
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16
            }}
          >
            {metricsCards.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>
        </div>

        {/* Charts and Details Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <EnhancedEducationChart />
          <RolesDisplay />
          {/* Academic Performance Card */}
          <ChartContainer>

            {/* HEADER */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20
              }}
            >
              <h4
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  margin: 0,
                  color: '#111827'
                }}
              >
                📊 Academic Performance
              </h4>

              <div
                style={{
                  fontSize: 12,
                  color: '#6B7280',
                  background: '#F3F4F6',
                  padding: '4px 10px',
                  borderRadius: 20
                }}
              >
                {educationData.length} Qualifications
              </div>
            </div>

            {/* BAR CHART */}
            {educationData.length === 0 ? (

              <div
                style={{
                  background: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: 16,
                  padding: '80px 20px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                No academic data found
              </div>

            ) : (

              <div
                style={{
                  position: 'relative',
                  background: '#fff',
                  padding: '30px 20px 60px 50px',
                }}
              >

                {/* Y AXIS LABELS */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 20,
                    bottom: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: '#6B7280'
                  }}
                >
                  {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map((v) => (
                    <div key={v}>{v}%</div>
                  ))}
                </div>

                {/* CHART AREA */}
                <div
                  style={{
                    height: 320,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-around',
                    borderLeft: '2px solid #D1D5DB',
                    borderBottom: '2px solid #D1D5DB',
                    position: 'relative'
                  }}
                >

                  {/* GRID LINES */}
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `${i * 10}%`,
                        borderTop: '1px solid #F3F4F6'
                      }}
                    />
                  ))}

                  {/* BARS */}

                  {sortedEducationData.map((edu, index) => {

                    const marks = Number(edu.marks || 0)

                    const barColor =
                      index % 2 === 0
                        ? '#1657C8'
                        : '#3CB179'

                    return (

                      <div
                        key={index}
                        style={{
                          width: '100%',
                          maxWidth: 160,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          position: 'relative',
                          zIndex: 2
                        }}
                      >

                        {/* MARKS */}
                        <div
                          style={{
                            marginBottom: 10,
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#111827'
                          }}
                        >
                          {marks}%
                        </div>

                        {/* BAR */}
                        <div
                          style={{
                            width: '70%',
                            height: `${marks}%`,
                            background: barColor,
                            borderRadius: '12px 12px 0 0',
                            transition: '0.4s ease',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
                          }}
                        />

                        {/* X AXIS LABELS OUTSIDE */}
                        <div
                          style={{
                            position: 'absolute',
                            bottom: -55,
                            textAlign: 'center'
                          }}
                        >

                          {/* LEVEL */}
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: '#374151',
                              textTransform: 'capitalize'
                            }}
                          >
                            {edu.level || '-'}
                          </div>



                        </div>

                      </div>
                    )
                  })}

                </div>

              </div>
            )}

          </ChartContainer>
          {/* Assessment Score Card */}
          {/* <div
    style={{
      padding: 16,
      borderRadius: 12,
      background: '#FFFFFF',
      border: '1px solid #eee',
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 14,
      }}
    >
      Assessment Score
    </div>

    {assessmentLoading ? (
      <div style={{ fontSize: 13, color: '#6B7280' }}>
        Loading assessment data...
      </div>
    ) : (() => {
      const chartData = [
        Number(assessmentData?.aptitude_score || 0),
        Number(assessmentData?.behavior_score || 0),
        Number(assessmentData?.communication_score || 0),
      ];

      const hasValidData = chartData.some(v => v > 0);

      if (!hasValidData) {
        return (
          <div
            style={{
              fontSize: 13,
              color: '#6B7280',
              textAlign: 'center',
              padding: '40px 0',
            }}
          >
            No Assessment data found
          </div>
        );
      }

      return (
        <div
          style={{
            position: 'relative',
            paddingLeft: 34,
            paddingBottom: 30,
            height: 260
          }}
        >
        
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 30,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              fontSize: 11,
              color: '#6B7280'
            }}
          >
            {[100, 80, 60, 40, 20, 0].map((v) => (
              <div key={v}>{v}</div>
            ))}
          </div>

        
          <div
            style={{
              height: '100%',
              borderLeft: '2px solid #D1D5DB',
              borderBottom: '2px solid #D1D5DB',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              position: 'relative'
            }}
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${i * 20}%`,
                  borderTop: '1px solid #F1F5F9'
                }}
              />
            ))}

            {[
              {
                label: 'Aptitude',
                value: Number(assessmentData?.aptitude_score || 0),
                color: '#3B82F6'
              },
              {
                label: 'Behavior',
                value: Number(assessmentData?.behavior_score || 0),
                color: '#10B981'
              },
              {
                label: 'Communication',
                value: Number(assessmentData?.communication_score || 0),
                color: '#8B5CF6'
              }
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  maxWidth: 90,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                <div
                  style={{
                    marginBottom: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#111827'
                  }}
                >
                  {item.value}
                </div>

                <div
                  style={{
                    width: '60%',
                    height: `${item.value}%`,
                    background: item.color,
                    borderRadius: '10px 10px 0 0',
                    transition: '0.4s ease'
                  }}
                />

                <div
                  style={{
                    position: 'absolute',
                    bottom: -28,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#475569',
                    textAlign: 'center'
                  }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    })()}
  </div> */}
          {/* Personal Information Card */}
          <ChartContainer>

            {/* HEADER */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24
              }}
            >
              <h4
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  margin: 0,
                  color: '#111827'
                }}
              >
                Personal Information
              </h4>
            </div>

            {/* CARD */}
            <div
              style={{
                background: '#fff',
                border: '1px solid #E5E7EB',
                padding: '8px 15px',
              }}
            >

              {[
                {
                  label: 'Email',
                  value: s?.admin?.email || 'N/A'
                },
                {
                  label: 'Phone',
                  value: s.studentPersonalProfile?.contact_number || 'N/A'
                },
                {
                  label: 'Gender',
                  value: s.studentPersonalProfile?.gender || 'N/A'
                },
                {
                  label: 'Address Line 1',
                  value: s.studentPersonalProfile?.corrAddrLine1 || 'N/A'
                },
                {
                  label: 'Address Line 2',
                  value: s.studentPersonalProfile?.corrAddrLine2 || 'N/A'
                },
                {
                  label: 'City',
                  value: s.studentPersonalProfile?.corrCity || 'N/A'
                },
                {
                  label: 'State',
                  value: s.studentPersonalProfile?.corrState || 'N/A'
                },
                {
                  label: 'Country',
                  value: s.studentPersonalProfile?.corrCountry || 'N/A'
                },
                {
                  label: 'Pincode',
                  value: s.studentPersonalProfile?.corrPostCode || 'N/A'
                }
              ].map((item, index, arr) => (

                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 0',
                    borderBottom:
                      index !== arr.length - 1
                        ? '1px solid #E2E8F0'
                        : 'none'
                  }}
                >

                  {/* LABEL */}
                  <div
                    style={{
                      fontSize: 15,
                      color: '#64748B',
                      fontWeight: 500,
                      minWidth: 85
                    }}
                  >
                    {item.label}
                  </div>

                  {/* VALUE */}
                  <div
                    style={{
                      fontSize: 13,
                      color: '#0F172A',
                      textAlign: 'right',
                      flex: 1,
                      wordBreak: 'break-word'
                    }}
                  >
                    {item.value}
                  </div>

                </div>
              ))}

            </div>

          </ChartContainer>
        </div>

        {/* Work Experience Section */}
        <div style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20
          }}>
            <h4 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#111827' }}>
              💼 Work Experience
            </h4>
            <div style={{
              background: '#6366f1',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600
            }}>
              {s.work_experience?.length || 0} Records
            </div>
          </div>

          {!s.work_experience?.length ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 0',
              color: '#9ca3af',
              fontSize: 14
            }}>
              No work experience data
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {s.work_experience.map((w, i) => {
                const isCurrently = w.isCurrentlyWorking
                const org = w.organization?.trim() || null
                const title = w.title?.trim() || null
                const role = w.role?.trim() || null
                const jobType = w.jobType?.trim() || null
                const location = w.work_location?.trim() || null
                const startDate = w.started_in || null
                const endDate = isCurrently ? 'Present' : (w.ended_in || null)
                const skills = Array.isArray(w.skill_set) ? w.skill_set.filter(sk => sk?.name) : []

                const rows = [
                  { label: 'Organization',  value: org },
                  { label: 'Title',         value: title },
                  { label: 'Role',          value: role },
                  { label: 'Job Type',      value: jobType },
                  { label: 'Work Location', value: location },
                  { label: 'Started',       value: startDate },
                  { label: 'Ended',         value: endDate },
                  { label: 'Skills',        value: skills.length ? skills.map(sk => sk.name).join(', ') : null },
                ]

                return (
                  <div key={w.id || i} style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 12,
                    background: '#fff',
                    overflow: 'hidden'
                  }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: '#F8FAFC',
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8,
                          background: isCurrently ? '#d1fae5' : '#EEF2FF',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18, flexShrink: 0
                        }}>
                          🏢
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                            {org || title || role || `Experience ${i + 1}`}
                          </div>
                          {(title || role) && org && (
                            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>
                              {title || role}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* {isCurrently && (
                        <span style={{
                          background: '#d1fae5', color: '#065f46',
                          fontSize: 11, fontWeight: 600,
                          padding: '3px 10px', borderRadius: 20,
                          whiteSpace: 'nowrap'
                        }}>
                          Currently Working
                        </span>
                      )} */}
                    </div>

                    {/* Detail rows */}
                    <div style={{ padding: '4px 16px' }}>
                      {rows.map(({ label, value }, ri) => (
                        <div key={label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: ri < rows.length - 1 ? '1px solid #F3F4F6' : 'none',
                          fontSize: 13
                        }}>
                          <span style={{ color: '#6B7280', fontWeight: 500, minWidth: 110 }}>{label}</span>
                          <span style={{ color: value ? '#111827' : '#9ca3af', textAlign: 'right', flex: 1 }}>
                            {value || 'Not Available'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

      </div>

    )
  }

  const [apiLogData, setApiLogData] = useState(null)
  const [apiLogLoading, setApiLogLoading] = useState(false)
  const [expandedRoles, setExpandedRoles] = useState({})
  const toggleRole = (key) => setExpandedRoles(prev => ({ ...prev, [key]: !prev[key] }))
  const fetchApiLog = async (studentId) => {
    if (!studentId) return
    setApiLogLoading(true)
    try {
      const resp = await candidateRequest.get(`/api/student-metrics/api-log/${studentId}`)
      setApiLogData(resp?.data?.data || null)
    } catch (err) {
      message.error('Failed to load API log')
      setApiLogData(null)
    } finally {
      setApiLogLoading(false)
    }
  }

  const [normalizedData, setNormalizedData] = useState([])
  const [normalizedLoading, setNormalizedLoading] = useState(false)
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const fetchNormalizedData = async (studentId) => {
    if (!studentId) return

    setNormalizedLoading(true)
    try {
      const resp = await candidateRequest.get(`/api/student-metrics/normalized/${studentId}`)

      // adjust if your response shape differs
      setNormalizedData(resp?.data?.data?.normalized_data || [])
    } catch (err) {
      message.error('Failed to load normalized data')
      setNormalizedData([])
    } finally {
      setNormalizedLoading(false)
    }
  }

  useEffect(() => {
    if (drawerOpen && selectedStudentId) {
      fetchAssessmentData(selectedStudentId)
    }
  }, [drawerOpen, selectedStudentId])

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialState = useMemo(() => {
    const base = _saved().filters ? { ..._saved().filters } : { ...DEFAULT_FILTERS }
    const dash = analyticsSession.dashboard
    if (dash.corporate_id && !base.work_corporate_ids?.length) base.work_corporate_ids = [dash.corporate_id]
    if (dash.institute_campus_id && !base.institute_campus_ids?.length) base.institute_campus_ids = [dash.institute_campus_id]
    analyticsSession.candidates.filters = base
    return { filters: base, searchValue: _saved().searchValue || '' }
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [searchValue, setSearchValue] = useState(initialState.searchValue)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [filters, setFilters] = useState(initialState.filters)
  const [filterDrawer, setFilterDrawer] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [clearInput, setClearInput] = useState(false)

  // persist filters into shared session
  useEffect(() => {
    analyticsSession.candidates.filters = { ...filters }
    analyticsSession.candidates.searchValue = searchValue
  }, [filters, searchValue])

  // Clear candidate filters on unmount so dashboard clicks always start fresh
  useEffect(() => () => { analyticsSession.candidates = { filters: null, searchValue: '', openTab: analyticsSession.candidates?.openTab } }, [])

  // Detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)

  // Download modal
  const [downloadModal, setDownloadModal] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [fileFormat, setFileFormat] = useState('xlsx')
  const [allDownloadCols, setAllDownloadCols] = useState([])
  const [colsLoading, setColsLoading] = useState(false)
  const [availableCols, setAvailableCols] = useState([])
  const [selectedCols, setSelectedCols] = useState([])
  const [availableChecks, setAvailableChecks] = useState({})
  const [selectedChecks, setSelectedChecks] = useState({})

  //drawer filter
  const [activeFilterTab, setActiveFilterTab] = useState('basic')

  //const [roleOptions, setRoleOptions] = useState([])

  const [roleOptions, setRoleOptions] = useState([])
  const [rolesPage, setRolesPage] = useState(1)
  const [rolesHasMore, setRolesHasMore] = useState(true)
  const [rolesSearch, setRolesSearch] = useState('')
  const [rolesLoading, setRolesLoading] = useState(false)
  const rolesSearchTimer = useRef(null)

  // ── Work Experience dropdown states ─────────────────────────────────────
  const [companyOptions, setCompanyOptions] = useState([])
  const [companyPage, setCompanyPage] = useState(1)
  const [companyHasMore, setCompanyHasMore] = useState(true)
  const [companySearch, setCompanySearch] = useState('')
  const [companyLoading, setCompanyLoading] = useState(false)
  const companySearchTimer = useRef(null)

  const [designationOptions, setDesignationOptions] = useState([])
  const [designationPage, setDesignationPage] = useState(1)
  const [designationHasMore, setDesignationHasMore] = useState(true)
  const [designationSearch, setDesignationSearch] = useState('')
  const [designationLoading, setDesignationLoading] = useState(false)
  const designationSearchTimer = useRef(null)

  const [industryOptions, setIndustryOptions] = useState([])
  const [industryPage, setIndustryPage] = useState(1)
  const [industryHasMore, setIndustryHasMore] = useState(true)
  const [industrySearch, setIndustrySearch] = useState('')
  const [industryLoading, setIndustryLoading] = useState(false)
  const industrySearchTimer = useRef(null)

  const [corporateOptions, setCorporateOptions] = useState(() => {
    const dashOpt = analyticsSession.dashboard.corporate_option
    return dashOpt ? [dashOpt] : []
  })
  const [corporatePage, setCorporatePage] = useState(1)
  const [corporateHasMore, setCorporateHasMore] = useState(true)
  const [corporateSearch, setCorporateSearch] = useState('')
  const [corporateLoading, setCorporateLoading] = useState(false)
  const corporateSearchTimer = useRef(null)
  const [passingYearOptions, setPassingYearOptions] = useState([])
  const [passingYearsLoading, setPassingYearsLoading] = useState(false)

  // ── Education / Location dropdown states ─────────────────────────────────
  const [campusOptions, setCampusOptions] = useState(() => {
    const dashOpt = analyticsSession.dashboard.institute_campus_option
    return dashOpt ? [dashOpt] : []
  })
  const [campusPage, setCampusPage] = useState(1)
  const [campusHasMore, setCampusHasMore] = useState(true)
  const [campusSearch, setCampusSearch] = useState('')
  const [campusLoading, setCampusLoading] = useState(false)
  const campusSearchTimer = useRef(null)
  const campusLabelMapRef = useRef((() => {
    const dashOpt = analyticsSession.dashboard.institute_campus_option
    return dashOpt ? { [dashOpt.value]: dashOpt.label } : {}
  })())

  const [educationLevelOptions, setEducationLevelOptions] = useState([])

  const [degreeOptions, setDegreeOptions] = useState([])
  const [degreePage, setDegreePage] = useState(1)
  const [degreeHasMore, setDegreeHasMore] = useState(true)
  const [degreeSearch, setDegreeSearch] = useState('')
  const [degreeLoading, setDegreeLoading] = useState(false)
  const degreeSearchTimer = useRef(null)

  const [deptOptions, setDeptOptions] = useState([])
  const [deptPage, setDeptPage] = useState(1)
  const [deptHasMore, setDeptHasMore] = useState(true)
  const [deptSearch, setDeptSearch] = useState('')
  const [deptLoading, setDeptLoading] = useState(false)
  const deptSearchTimer = useRef(null)

  const [cityOptions, setCityOptions] = useState([])
  const [cityPage, setCityPage] = useState(1)
  const [cityHasMore, setCityHasMore] = useState(true)
  const [citySearch, setCitySearch] = useState('')
  const [cityLoading, setCityLoading] = useState(false)
  const citySearchTimer = useRef(null)

  const [stateOptions, setStateOptions] = useState([])
  const [statePage, setStatePage] = useState(1)
  const [stateHasMore, setStateHasMore] = useState(true)
  const [stateSearch, setStateSearch] = useState('')
  const [stateLoading, setStateLoading] = useState(false)
  const stateSearchTimer = useRef(null)

  const fetchRoles = useCallback(async (page = 1, search = '', append = false) => {
    setRolesLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/roles', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setRoleOptions(prev => mergeRoleOptions(append ? [...prev, ...items] : items))
      setRolesHasMore(items.length === 10)
      setRolesPage(page)
    } catch {
      // silent
    } finally {
      setRolesLoading(false)
    }
  }, [])

  const fetchWorkCompanies = useCallback(async (page = 1, search = '', append = false) => {
    setCompanyLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/work-companies', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setCompanyOptions(prev => append ? [...prev, ...items] : items)
      setCompanyHasMore(items.length === 10)
      setCompanyPage(page)
    } catch {
      // silent
    } finally {
      setCompanyLoading(false)
    }
  }, [])

  const fetchWorkDesignations = useCallback(async (page = 1, search = '', append = false) => {
    setDesignationLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/work-designations', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setDesignationOptions(prev => append ? [...prev, ...items] : items)
      setDesignationHasMore(items.length === 10)
      setDesignationPage(page)
    } catch {
      // silent
    } finally {
      setDesignationLoading(false)
    }
  }, [])

  const fetchWorkIndustries = useCallback(async (page = 1, search = '', append = false) => {
    setIndustryLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/work-industries', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setIndustryOptions(prev => append ? [...prev, ...items] : items)
      setIndustryHasMore(items.length === 10)
      setIndustryPage(page)
    } catch {
      // silent
    } finally {
      setIndustryLoading(false)
    }
  }, [])

  const fetchWorkCorporates = useCallback(async (page = 1, search = '', append = false) => {
    setCorporateLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/work-corporates', {
        params: { page, limit: 10, search: search || undefined },
      })
      // const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      const items = (resp?.data?.data || []).map(r => ({ value: r.id, label: r.name }))
      setCorporateOptions(prev => append ? [...prev, ...items] : items)
      setCorporateHasMore(items.length === 10)
      setCorporatePage(page)
    } catch {
      // silent
    } finally {
      setCorporateLoading(false)
    }
  }, [])

  const fetchPassingYears = useCallback(async () => {
    setPassingYearsLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/passing-years')
      // Keep the UI safe during a staggered rollout too: older API instances
      // may still return malformed timestamps as years such as 2380.
      const earliestYear = 1950
      const latestYear = new Date().getFullYear() + 10
      const items = (resp?.data?.data || [])
        .filter(y => Number.isInteger(Number(y)) && Number(y) >= earliestYear && Number(y) <= latestYear)
        .map(y => ({ value: Number(y), label: String(y) }))
      setPassingYearOptions(items)
    } catch {
      // silent
    } finally {
      setPassingYearsLoading(false)
    }
  }, [])

  const fetchCampuses = useCallback(async (page = 1, search = '', append = false) => {
    setCampusLoading(true)
    try {
      // Use the analytics master endpoint rather than Elasticsearch. The master
      // query explicitly applies `is_active = true`, so an inactive campus can
      // never appear in this filter or be folded into an active option.
      const resp = await candidateRequest.get('/api/student-metrics/master-institutes', {
        params: { page, limit: 10, search: search || undefined },
      })
      const rawItems = (resp?.data?.data || []).map(c => ({ value: c?.id, label: c?.name }))
      // Active duplicate master rows are represented as one option. Its value
      // contains all active IDs, so selection still finds every linked student.
      setCampusOptions(prev => {
        const all = append ? [...prev, ...rawItems] : rawItems
        const merged = new Map()
        all.forEach(item => {
          if (!item.value || !item.label) return
          const key = item.label.trim().toLocaleLowerCase().replace(/\s+/g, ' ')
          const current = merged.get(key)
          const ids = new Set((current?.value || '').split('||').filter(Boolean))
          String(item.value).split('||').filter(Boolean).forEach(id => ids.add(id))
          merged.set(key, { value: [...ids].join('||'), label: current?.label || item.label.trim() })
        })
        return [...merged.values()]
      })
      setCampusHasMore(rawItems.length === 10)
      setCampusPage(page)
    } catch { } finally { setCampusLoading(false) }
  }, [])

  const EDU_LABEL_MAP = { ug: 'UG', pg: 'PG', phd: 'PhD', iti: 'ITI', puc: 'PUC', pd: 'PD', p_g_diploma: 'P.G. Diploma', tenth: '10th', twelfth: '12th' }
  const formatEduLabel = v => EDU_LABEL_MAP[v] || v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  const fetchEducationLevels = useCallback(async () => {
    try {
      const resp = await candidateRequest.get('/api/student-metrics/dashboard-summary')
      const dist = resp?.data?.data?.education_dist || []
      const items = dist.map(item => {
        const val = item.label.toLowerCase()
        return { value: val, label: formatEduLabel(val) }
      })
      setEducationLevelOptions(items)
    } catch { }
  }, [])

  // Degree/Department options come from the candidates themselves, not from the
  // institute.degrees/streams masters. The list filter matches on
  // current_course.degree/department text, so sourcing options from the master
  // offered thousands of degrees no candidate holds while hiding common ones
  // (B.Tech/MBA/MCA sit at status 0). Every option here returns at least one row.
  const fetchDegrees = useCallback(async (page = 1, search = '', append = false) => {
    setDegreeLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/degrees', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(toOption)
      setDegreeOptions(prev => append ? mergeOptionsByValue(prev, items) : items)
      setDegreeHasMore(items.length === 10)
      setDegreePage(page)
    } catch { } finally { setDegreeLoading(false) }
  }, [])

  const fetchDepts = useCallback(async (page = 1, search = '', append = false) => {
    setDeptLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/departments', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(toOption)
      setDeptOptions(prev => append ? mergeOptionsByValue(prev, items) : items)
      setDeptHasMore(items.length === 10)
      setDeptPage(page)
    } catch { } finally { setDeptLoading(false) }
  }, [])

  const fetchCities = useCallback(async (page = 1, search = '', append = false) => {
    setCityLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/cities', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setCityOptions(prev => append ? [...prev, ...items] : items)
      setCityHasMore(items.length === 10)
      setCityPage(page)
    } catch { } finally { setCityLoading(false) }
  }, [])

  const fetchStates = useCallback(async (page = 1, search = '', append = false) => {
    setStateLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/states', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r, label: r }))
      setStateOptions(prev => append ? [...prev, ...items] : items)
      setStateHasMore(items.length === 10)
      setStatePage(page)
    } catch { } finally { setStateLoading(false) }
  }, [])

  // ── Build filter params for API ──────────────────────────────────────────
  const filterParams = useMemo(() => ({
    search: searchValue || undefined,
    gender: filters.gender || undefined,
    status: filters.status != null ? filters.status : undefined,
    passed_out: filters.passed_out != null ? filters.passed_out : undefined,
    profile_min: filters.profile[0] !== 0 ? filters.profile[0] : undefined,
    profile_max: filters.profile[1] !== 100 ? filters.profile[1] : undefined,
    institute_campus: filters.institute_campus?.length ? filters.institute_campus.join('||') : undefined,
    institute_campus_id: filters.institute_campus_ids?.length
      ? filters.institute_campus_ids.join('||')
      : (filters.institute_campus_id || undefined),
    education_level: filters.education_level || undefined,
    degree: filters.degree?.length ? filters.degree.join('||') : undefined,
    department: filters.department?.length ? filters.department.join('||') : undefined,
    city: filters.city?.length ? filters.city.join('||') : undefined,
    state: filters.state?.length ? filters.state.join('||') : undefined,
    is_exp_candidate: filters.is_exp_candidate != null ? filters.is_exp_candidate : undefined,
    roles: filters.roles?.length ? filters.roles.join('||') : undefined,
    role_search: filters.roleSearch?.trim() ? filters.roleSearch.trim() : undefined,
    work_company: filters.work_company?.length ? filters.work_company.join('||') : undefined,
    work_designation: filters.work_designation?.length ? filters.work_designation.join('||') : undefined,
    work_industry: filters.work_industry?.length ? filters.work_industry.join('||') : undefined,
    work_location: filters.work_location || undefined,
    work_started_from: filters.work_started_from || undefined,
    work_started_to: filters.work_started_to || undefined,
    notice_period: filters.notice_period || undefined,
    work_corporate_ids: filters.work_corporate_ids?.length ? filters.work_corporate_ids.join('||') : undefined,
    salary_min: filters.salary_min !== '' && filters.salary_max !== '' ? parseFloat(filters.salary_min) : undefined,
    salary_max: filters.salary_max !== '' && filters.salary_min !== '' ? parseFloat(filters.salary_max) : undefined,
    passing_years: filters.passing_years?.length ? filters.passing_years.join('||') : undefined,
  }), [searchValue, filters])

  // ── Active filter count for badge ────────────────────────────────────────
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.gender) count++
    if (filters.passed_out != null) count++
    if (filters.profile[0] !== 0 || filters.profile[1] !== 100) count++
    if (filters.institute_campus?.length || filters.institute_campus_id || filters.institute_campus_ids?.length) count++
    if (filters.education_level) count++
    if (filters.degree?.length) count++
    if (filters.department?.length) count++
    if (filters.city?.length) count++
    if (filters.state?.length) count++
    if (filters.is_exp_candidate != null) count++
    if (filters.roles?.length) count++
    if (filters.roleSearch?.trim()) count++
    if (filters.work_company?.length) count++
    if (filters.work_designation?.length) count++
    if (filters.work_industry?.length) count++
    if (filters.work_location) count++
    if (filters.work_started_from) count++
    if (filters.work_started_to) count++
    if (filters.notice_period) count++
    if (filters.work_corporate_ids?.length) count++
    if (filters.salary_min !== '' && filters.salary_max !== '') count++
    if (filters.passing_years?.length) count++
    return count
  }, [filters])

  const fetchStudents = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics', {
        params: { page: page + 1, limit: PAGE_SIZE, ...filterParams },
      })
      setData(resp?.data?.data || [])
      setTotal(resp?.data?.total || 0)
    } catch {
      message.error('Failed to load students')
      setData([])
      setTotal(0)
    } finally {
      setTimeout(() => setLoading(false), 400)
    }
  }, [page, filterParams])

  useEffect(() => { fetchStudents() }, [fetchStudents])
  useEffect(() => { setPage(0) }, [searchValue, filters])



  const handleView = async (row) => {
    setDrawerOpen(true)
    setDrawerLoading(true)
    setSelectedStudent(null)
    setSelectedStudentId(row.id)
    setSelectedRow(row)
    try {
      const resp = await candidateRequest.get(`/api/student-metrics/${row.id}`)
      setSelectedStudent(resp?.data?.data || null)
    } catch {
      message.error('Failed to load student details')
    } finally {
      setDrawerLoading(false)
    }
  }

  const onResetFilters = () => {
    analyticsSession.candidates = { filters: { ...DEFAULT_FILTERS }, searchValue: '' }
    analyticsSession.dashboard = {
      institute_campus_id: null, institute_campus_name: null, institute_campus_option: null,
      corporate_id: null, corporate_name: null, corporate_option: null,
    }
    setFilters({ ...DEFAULT_FILTERS })
    setSearchValue('')
    setClearInput(prev => !prev)
    setPage(0)
  }

  // Clears only the filters — preserves the search input value
  const onClearFiltersOnly = () => {
    analyticsSession.candidates = { filters: { ...DEFAULT_FILTERS }, searchValue }
    analyticsSession.dashboard = {
      institute_campus_id: null, institute_campus_name: null, institute_campus_option: null,
      corporate_id: null, corporate_name: null, corporate_option: null,
    }
    setFilters({ ...DEFAULT_FILTERS })
    setPage(0)
  }

  // ── Download handlers ────────────────────────────────────────────────────
  const resetDownloadModal = () => {
    setAvailableCols([...allDownloadCols])
    setSelectedCols([])
    setAvailableChecks({})
    setSelectedChecks({})
    setFileFormat('xlsx')
    setDownloadModal(false)
    setDownloadLoading(false)
  }

  const moveCheckedToSelected = () => {
    const checked = availableCols.filter(c => availableChecks[c.slug_name])
    setSelectedCols(prev => [...prev, ...checked])
    setAvailableCols(prev => prev.filter(c => !availableChecks[c.slug_name]))
    setAvailableChecks({})
  }

  const moveCheckedToAvailable = () => {
    const checked = selectedCols.filter(c => selectedChecks[c.slug_name])
    setAvailableCols(prev => [...prev, ...checked])
    setSelectedCols(prev => prev.filter(c => !selectedChecks[c.slug_name]))
    setSelectedChecks({})
  }

  const onDragEnd = (result) => {
    if (!result.destination) return
    const { source: src, destination: dest } = result
    if (src.droppableId === dest.droppableId) {
      const list = src.droppableId === 'available' ? [...availableCols] : [...selectedCols]
      const [moved] = list.splice(src.index, 1)
      list.splice(dest.index, 0, moved)
      src.droppableId === 'available' ? setAvailableCols(list) : setSelectedCols(list)
    } else {
      const srcList = src.droppableId === 'available' ? [...availableCols] : [...selectedCols]
      const destList = dest.droppableId === 'available' ? [...availableCols] : [...selectedCols]
      const [moved] = srcList.splice(src.index, 1)
      destList.splice(dest.index, 0, moved)
      if (src.droppableId === 'available') { setAvailableCols(srcList); setSelectedCols(destList) }
      else { setSelectedCols(srcList); setAvailableCols(destList) }
    }
  }

  const handleDownload = async () => {
    if (!selectedCols.length) { message.warning('Select at least one column'); return }
    setDownloadLoading(true)
    try {
      const resp = await candidateRequest.post('/api/student-metrics/download/async', {
        columns: selectedCols.map(c => c.slug_name),
        file_format: fileFormat,
        user_id: currentUserId,
        notify_email: currentUserEmail || null,
        search: searchValue || null,
        gender: filters.gender || null,
        status: filters.status ?? null,
        passed_out: filters.passed_out ?? null,
        profile_min: filters.profile[0] !== 0 ? filters.profile[0] : null,
        profile_max: filters.profile[1] !== 100 ? filters.profile[1] : null,
        institute_campus: filters.institute_campus?.length ? filters.institute_campus : null,
        institute_campus_id: filters.institute_campus_id || (filters.institute_campus_ids?.length ? filters.institute_campus_ids.join('||') : null),
        education_level: filters.education_level || null,
        degree: filters.degree?.length ? filters.degree : null,
        department: filters.department?.length ? filters.department : null,
        city: filters.city?.length ? filters.city : null,
        state: filters.state?.length ? filters.state : null,
        is_exp_candidate: filters.is_exp_candidate ?? null,
        roles: filters.roles?.length ? filters.roles : null,
        role_search: filters.roleSearch?.trim() ? filters.roleSearch.trim() : null,
        work_company: filters.work_company?.length ? filters.work_company : null,
        work_designation: filters.work_designation?.length ? filters.work_designation : null,
        work_industry: filters.work_industry?.length ? filters.work_industry : null,
        work_location: filters.work_location || null,
        work_started_from: filters.work_started_from || null,
        work_started_to: filters.work_started_to || null,
        notice_period: filters.notice_period || null,
        work_corporate_ids: filters.work_corporate_ids?.length ? filters.work_corporate_ids : null,
        salary_min: filters.salary_min !== '' && filters.salary_max !== '' ? parseFloat(filters.salary_min) : null,
        salary_max: filters.salary_max !== '' && filters.salary_min !== '' ? parseFloat(filters.salary_max) : null,
        passing_years: filters.passing_years?.length ? filters.passing_years : null,
      })
      const { job_id } = resp.data
      resetDownloadModal()

      // Overwrite any existing exportStatus — new job replaces old
      setExportStatus({
        jobId: job_id,
        status: 'pending',
        fileFormat,
        requestedAt: new Date().toISOString(),
        notified: false,
        notifyEmail: currentUserEmail || null,
        errorMessage: null,
      })
      startPolling(job_id)
    } catch {
      message.error('Failed to start export')
    } finally {
      setDownloadLoading(false)
    }
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'STUDENT', key: 'full_name', width: '28%',
      render: (_, row) => (
        <div>
          <RowNumberData style={{ fontWeight: 'bold', fontSize: 14 }}>
            {row?.full_name || `${row?.first_name || ''} ${row?.last_name || ''}`.trim() || '-'}
          </RowNumberData>
          <RowNumberEmailData>{row?.primary_email || '-'}</RowNumberEmailData>
        </div>
      ),
    },
    {
      title: 'MOBILE', key: 'contact_number', width: '14%',
      render: (_, row) => <RowNumberData>{row?.contact_number || '-'}</RowNumberData>,
    },
    {
      title: 'DOB', key: 'dob', width: '12%',
      render: (_, row) => <RowNumberData>{fmtDate(row?.dob)}</RowNumberData>,
    },
    {
      title: 'GENDER', key: 'gender', width: '10%',
      render: (_, row) => (
        <RowNumberData>
          {row?.gender
            ? String(row.gender).charAt(0).toUpperCase() + String(row.gender).slice(1).toLowerCase()
            : '-'}
        </RowNumberData>
      ),
    },
    {
      title: 'ACTION', key: 'action', width: '10%', align: 'center',
      // render: (_, row) => (
      //   <Button.Secondary height="32px" text="View" onClick={() => handleView(row)} />
      // ),
      render: (_, row) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="View">
            <EyeOutlined
              style={{ fontSize: 18, color: '#3249D7', cursor: 'pointer' }}
              onClick={() => handleView(row)}
            />
          </Tooltip>
        </div>
      ),
    },
  ]

  const s = selectedStudent

  // ── Column picker list ───────────────────────────────────────────────────
  const ColList = ({ droppableId, items, checks, setChecks }) => (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <div
          ref={provided.innerRef} {...provided.droppableProps}
          style={{
            flex: 1, height: 300, overflowY: 'auto',
            border: '1px solid #eee', padding: 10,
            background: droppableId === 'selected' ? '#f0f7ff' : '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong style={{ fontSize: 13 }}>
              {droppableId === 'available' ? 'Available Columns' : `Selected (${items.length})`}
            </strong>
            <Checkbox
              checked={items.length > 0 && items.every(c => checks[c.slug_name])}
              indeterminate={items.some(c => checks[c.slug_name]) && !items.every(c => checks[c.slug_name])}
              onChange={e => {
                const next = {}
                items.forEach(c => { next[c.slug_name] = e.target.checked })
                setChecks(next)
              }}
            >
              All
            </Checkbox>
          </div>
          {items.map((col, idx) => (
            <Draggable key={col.slug_name} draggableId={col.slug_name} index={idx}>
              {(prov) => (
                <div
                  ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                  style={{
                    padding: '7px 10px', marginBottom: 6, borderRadius: 6, cursor: 'grab',
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: droppableId === 'selected' ? '#d6e4ff' : '#fafafa',
                    border: droppableId === 'selected' ? '1px solid #91caff' : '1px solid #ddd',
                    ...prov.draggableProps.style,
                  }}
                >
                  <Checkbox
                    checked={checks[col.slug_name] || false}
                    onChange={e => setChecks(prev => ({ ...prev, [col.slug_name]: e.target.checked }))}
                  />
                  <span style={{ fontSize: 13 }}>{col.display_name}</span>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  )

  return (
    <SectionWrapper column>
      <TopHeader row spaceBetween alignCenter style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <TopHeading flexStart>Live Candidates List</TopHeading>
        {/* <div style={{ display: 'flex', gap: 10 }}>
          <Button.Primary
            height="36px"
            text={downloadLoading ? 'Loading...' : 'Download'}
            onClick={() => setDownloadModal(true)}
            // disabled={downloadLoading}
            disabled={downloadLoading || (exportStatus && (exportStatus.status === 'pending' || exportStatus.status === 'processing'))}
          />
          <Button.Secondary
            height="36px" text="Refresh"
            onClick={fetchStudents} disabled={loading}
          />
        </div> */}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button.Primary
            height="36px"
            text={downloadLoading ? 'Loading...' : 'Download'}
            onClick={() => setDownloadModal(true)}
            disabled={downloadLoading || (exportStatus && (exportStatus.status === 'pending' || exportStatus.status === 'processing'))}
          />
          <Button.Secondary
            height="36px" text="Refresh"
            onClick={fetchStudents} disabled={loading}
          />
        </div>

        <ExportStatusPanel
          status={exportStatus}
          onDismiss={() => setExportStatus(null)}
        />
      </TopHeader>

      {/* Add this style tag once anywhere in the component return */}
      <style>{`
        @keyframes exportPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      {/* ─── Collapsible Filter Bar ───────────────────────────────────────── */}
      {/* <div style={{ marginBottom: 4 }}> */}
      <div style={{ marginBottom: 10, paddingLeft: 2, paddingRight: 2}}>
        {/* Toggle row — sits right below the Download/Refresh row */}
        <div
          onClick={() => {
            const next = !filterOpen
            setFilterOpen(next)
            if (next) {
              if (cityOptions.length === 0) fetchCities(1, '')
              if (stateOptions.length === 0) fetchStates(1, '')
              if (educationLevelOptions.length === 0) fetchEducationLevels()
              if (campusOptions.length === 0) fetchCampuses(1, '')
              if (degreeOptions.length === 0) fetchDegrees(1, '')
              if (deptOptions.length === 0) fetchDepts(1, '')
              if (passingYearOptions.length === 0) fetchPassingYears()
              if (roleOptions.length === 0) fetchRoles(1, '')
              if (companyOptions.length === 0) fetchWorkCompanies(1, '')
              if (designationOptions.length === 0) fetchWorkDesignations(1, '')
              if (industryOptions.length === 0) fetchWorkIndustries(1, '')
              if (corporateOptions.length === 0) fetchWorkCorporates(1, '')
            }
          }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 16px',
            background: '#f8f9fb',
            border: '1px solid #e5e7eb',
            borderRadius: filterOpen ? '8px 8px 0 0' : 8,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f1f3f7'}
          onMouseLeave={e => e.currentTarget.style.background = '#f8f9fb'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Filter icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Filters</span>
            {activeFilterCount > 0 && (
              <span style={{
                background: '#3249D7', color: '#fff',
                borderRadius: 999, fontSize: 11, fontWeight: 700,
                padding: '1px 7px', lineHeight: '18px',
              }}>{activeFilterCount}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {activeFilterCount > 0 && (
              <span
                onClick={e => { e.stopPropagation(); onClearFiltersOnly() }}
                style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear all
              </span>
            )}
            {/* Chevron */}
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ transition: 'transform 0.2s', transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* Expanded filter panel */}
        {filterOpen && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            background: '#fff',
            padding: '20px 20px 16px',
          }}>
            {/* All filters in a single responsive grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '40px 24px',
              alignItems: 'start',
            }}>

              {/* Education Level */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Education Level</div>
                <Select style={{ width: '100%' }} placeholder="Select level" allowClear value={filters.education_level}
                  onChange={val => setFilters(p => ({ ...p, education_level: val ?? null }))}
                  options={educationLevelOptions} />
              </div>

              {/* Institute Campus */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Institute Campus</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Search (3+ chars)..."
                  allowClear value={filters.institute_campus_ids}
                  onChange={(val, opts) => {
                    const next = val ?? []
                    ;(opts || []).forEach(o => { if (o.label) campusLabelMapRef.current[o.value] = o.label })
                    setFilters(p => ({ ...p, institute_campus_ids: next }))
                    setCampusOptions(prev => {
                      const s = new Set(next)
                      return [...next.map(v => { const f = prev.find(o => o.value === v); return f || { value: v, label: campusLabelMapRef.current[v] || String(v) } }), ...prev.filter(o => !s.has(o.value))]
                    })
                  }}
                  onDropdownVisibleChange={open => { if (open) { setCampusSearch(''); setCampusPage(1); setCampusHasMore(true); fetchCampuses(1, '') } }}
                  options={campusOptions} maxTagCount="responsive" showSearch filterOption={false} loading={campusLoading}
                  onSearch={val => { clearTimeout(campusSearchTimer.current); if (val.length < 3) return; campusSearchTimer.current = setTimeout(() => { setCampusSearch(val); fetchCampuses(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, institute_campus_ids: [] })); setCampusOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && campusHasMore && !campusLoading) fetchCampuses(campusPage + 1, campusSearch, true) }}
                  notFoundContent={campusLoading ? <Spin size="small" /> : 'No institutes found'}
                />
              </div>

              {/* Degree */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Degree</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Search (2+ chars)..."
                  allowClear value={filters.degree}
                  onChange={val => setFilters(p => ({ ...p, degree: val ?? [] }))}
                  onDropdownVisibleChange={open => { if (open) { setDegreeSearch(''); setDegreePage(1); setDegreeHasMore(true); fetchDegrees(1, '') } }}
                  options={degreeOptions} maxTagCount="responsive" showSearch filterOption={false} loading={degreeLoading}
                  onSearch={val => { clearTimeout(degreeSearchTimer.current); if (val.length < 2) return; degreeSearchTimer.current = setTimeout(() => { setDegreeSearch(val); fetchDegrees(1, val) }, 400) }}
                  onClear={() => setFilters(p => ({ ...p, degree: [] }))}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && degreeHasMore && !degreeLoading) fetchDegrees(degreePage + 1, degreeSearch, true) }}
                  notFoundContent={degreeLoading ? <Spin size="small" /> : 'No degrees found'}
                />
              </div>

              {/* Department */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Department</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Search (3+ chars)..."
                  allowClear value={filters.department}
                  onChange={val => setFilters(p => ({ ...p, department: val ?? [] }))}
                  onDropdownVisibleChange={open => { if (open) { setDeptSearch(''); setDeptPage(1); setDeptHasMore(true); fetchDepts(1, '') } }}
                  options={deptOptions} maxTagCount="responsive" showSearch filterOption={false} loading={deptLoading}
                  onSearch={val => { clearTimeout(deptSearchTimer.current); if (val.length < 2) return; deptSearchTimer.current = setTimeout(() => { setDeptSearch(val); fetchDepts(1, val) }, 400) }}
                  onClear={() => setFilters(p => ({ ...p, department: [] }))}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && deptHasMore && !deptLoading) fetchDepts(deptPage + 1, deptSearch, true) }}
                  notFoundContent={deptLoading ? <Spin size="small" /> : 'No departments found'}
                />
              </div>

              {/* Year of Passing */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Year of Passing</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select year(s)..."
                  allowClear value={filters.passing_years}
                  onChange={val => setFilters(p => ({ ...p, passing_years: val ?? [] }))}
                  maxTagCount="responsive" loading={passingYearsLoading}
                  options={passingYearOptions}
                  notFoundContent={passingYearsLoading ? <Spin size="small" /> : 'No years found'}
                />
              </div>

              {/* Current City */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Current City</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Search (3+ chars)..."
                  allowClear value={filters.city}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, city: next })); setCityOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setCitySearch(''); setCityPage(1); setCityHasMore(true); fetchCities(1, '') } }}
                  options={cityOptions} maxTagCount="responsive" showSearch filterOption={false} loading={cityLoading}
                  onSearch={val => { clearTimeout(citySearchTimer.current); if (val.length < 3) return; citySearchTimer.current = setTimeout(() => { setCitySearch(val); fetchCities(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, city: [] })); setCityOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && cityHasMore && !cityLoading) fetchCities(cityPage + 1, citySearch, true) }}
                  notFoundContent={cityLoading ? <Spin size="small" /> : 'No cities found'}
                />
              </div>

              {/* Current State */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Current State</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Search (3+ chars)..."
                  allowClear value={filters.state}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, state: next })); setStateOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setStateSearch(''); setStatePage(1); setStateHasMore(true); fetchStates(1, '') } }}
                  options={stateOptions} maxTagCount="responsive" showSearch filterOption={false} loading={stateLoading}
                  onSearch={val => { clearTimeout(stateSearchTimer.current); if (val.length < 3) return; stateSearchTimer.current = setTimeout(() => { setStateSearch(val); fetchStates(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, state: [] })); setStateOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && stateHasMore && !stateLoading) fetchStates(statePage + 1, stateSearch, true) }}
                  notFoundContent={stateLoading ? <Spin size="small" /> : 'No states found'}
                />
              </div>

              {/* Smart Role Search — type a role, get all matching candidates (no chip-picking) */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Role Search
                  <span style={{ textTransform: 'none', fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>— type a role, get all matching candidates</span>
                </div>
                <Input.Search
                  key={`rolesearch-${filters.roleSearch}`}
                  allowClear
                  enterButton
                  placeholder="e.g. full stack, software developer"
                  defaultValue={filters.roleSearch}
                  onSearch={val => setFilters(p => ({ ...p, roleSearch: (val || '').trim() }))}
                />
              </div>

              {/* Roles Applied */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Roles Applied</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select roles..."
                  allowClear value={filters.roles}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, roles: next })); setRoleOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setRolesSearch(''); setRolesPage(1); setRolesHasMore(true); fetchRoles(1, '') } }}
                  options={roleOptions} maxTagCount="responsive" showSearch filterOption={false} loading={rolesLoading}
                  onSearch={val => { clearTimeout(rolesSearchTimer.current); if (val.length < 3) return; rolesSearchTimer.current = setTimeout(() => { setRolesSearch(val); fetchRoles(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, roles: [] })); setRoleOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && rolesHasMore && !rolesLoading) fetchRoles(rolesPage + 1, rolesSearch, true) }}
                  notFoundContent={rolesLoading ? <Spin size="small" /> : 'No roles found'}
                />
              </div>

              {/* Status */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Status</div>
                <Select style={{ width: '100%' }} placeholder="Select status" allowClear value={filters.status}
                  onChange={val => setFilters(p => ({ ...p, status: val ?? null }))}
                  options={[
                    { value: 2, label: 'Active' }, { value: -2, label: 'Inactive' },
                    { value: 1, label: 'Pending' }, { value: -1, label: 'Blacklisted' },
                    { value: -3, label: 'Not Interested' }, { value: 0, label: 'Unknown' },
                  ]}
                />
              </div>

              

              {/* Company */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Company</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select company..."
                  allowClear value={filters.work_company}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, work_company: next })); setCompanyOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setCompanySearch(''); setCompanyPage(1); setCompanyHasMore(true); fetchWorkCompanies(1, '') } }}
                  options={companyOptions} maxTagCount="responsive" showSearch filterOption={false} loading={companyLoading}
                  onSearch={val => { clearTimeout(companySearchTimer.current); if (val.length < 3) return; companySearchTimer.current = setTimeout(() => { setCompanySearch(val); fetchWorkCompanies(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, work_company: [] })); setCompanyOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && companyHasMore && !companyLoading) fetchWorkCompanies(companyPage + 1, companySearch, true) }}
                  notFoundContent={companyLoading ? <Spin size="small" /> : 'No companies found'}
                />
              </div>

              {/* Designation */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Designation</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select designation..."
                  allowClear value={filters.work_designation}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, work_designation: next })); setDesignationOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setDesignationSearch(''); setDesignationPage(1); setDesignationHasMore(true); fetchWorkDesignations(1, '') } }}
                  options={designationOptions} maxTagCount="responsive" showSearch filterOption={false} loading={designationLoading}
                  onSearch={val => { clearTimeout(designationSearchTimer.current); if (val.length < 3) return; designationSearchTimer.current = setTimeout(() => { setDesignationSearch(val); fetchWorkDesignations(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, work_designation: [] })); setDesignationOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && designationHasMore && !designationLoading) fetchWorkDesignations(designationPage + 1, designationSearch, true) }}
                  notFoundContent={designationLoading ? <Spin size="small" /> : 'No designations found'}
                />
              </div>

              {/* Industry */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Industry</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select industry..."
                  allowClear value={filters.work_industry}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, work_industry: next })); setIndustryOptions(prev => { const s = new Set(next); return [...next.map(v => ({ value: v, label: v })), ...prev.filter(o => !s.has(o.value))] }) }}
                  onDropdownVisibleChange={open => { if (open) { setIndustrySearch(''); setIndustryPage(1); setIndustryHasMore(true); fetchWorkIndustries(1, '') } }}
                  options={industryOptions} maxTagCount="responsive" showSearch filterOption={false} loading={industryLoading}
                  onSearch={val => { clearTimeout(industrySearchTimer.current); if (val.length < 3) return; industrySearchTimer.current = setTimeout(() => { setIndustrySearch(val); fetchWorkIndustries(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, work_industry: [] })); setIndustryOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && industryHasMore && !industryLoading) fetchWorkIndustries(industryPage + 1, industrySearch, true) }}
                  notFoundContent={industryLoading ? <Spin size="small" /> : 'No industries found'}
                />
              </div>

              {/* Work Location */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Work Location</div>
                <Input placeholder="e.g. Chennai" value={filters.work_location}
                  onChange={e => setFilters(p => ({ ...p, work_location: e.target.value }))} />
              </div>

              {/* Job Application Date */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Job Application Date</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <DatePicker placeholder="From" style={{ flex: 1 }}
                    value={filters.work_started_from ? moment(filters.work_started_from, 'YYYY-MM-DD') : null}
                    onChange={(date) => setFilters(p => ({ ...p, work_started_from: date ? date.format('YYYY-MM-DD') : null }))} format="DD/MM/YYYY" />
                  <DatePicker placeholder="To" style={{ flex: 1 }}
                    value={filters.work_started_to ? moment(filters.work_started_to, 'YYYY-MM-DD') : null}
                    onChange={(date) => setFilters(p => ({ ...p, work_started_to: date ? date.format('YYYY-MM-DD') : null }))}
                    disabledDate={(current) => filters.work_started_from ? current && current < moment(filters.work_started_from, 'YYYY-MM-DD') : false}
                    format="DD/MM/YYYY" />
                </div>
              </div>

              {/* Corporate Entity */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Corporate Entity</div>
                <Select mode="multiple" style={{ width: '100%' }} placeholder="Select corporate..."
                  allowClear value={filters.work_corporate_ids}
                  onChange={(val, opts) => {
                    const next = val ?? []
                    setFilters(p => ({ ...p, work_corporate_ids: next }))
                    setCorporateOptions(prev => {
                      const s = new Set(next)
                      return [...next.map(v => { const f = prev.find(o => o.value === v) || (opts||[]).find(o => o.value === v); return f || { value: v, label: String(v) } }), ...prev.filter(o => !s.has(o.value))]
                    })
                  }}
                  onDropdownVisibleChange={open => { if (open) { setCorporateSearch(''); setCorporatePage(1); setCorporateHasMore(true); fetchWorkCorporates(1, '') } }}
                  options={corporateOptions} maxTagCount="responsive" showSearch filterOption={false} loading={corporateLoading}
                  onSearch={val => { clearTimeout(corporateSearchTimer.current); if (val.length < 3) return; corporateSearchTimer.current = setTimeout(() => { setCorporateSearch(val); fetchWorkCorporates(1, val) }, 400) }}
                  onClear={() => { setFilters(p => ({ ...p, work_corporate_ids: [] })); setCorporateOptions([]) }}
                  onPopupScroll={e => { const { scrollTop, scrollHeight, clientHeight } = e.target; if (scrollHeight - scrollTop <= clientHeight + 20 && corporateHasMore && !corporateLoading) fetchWorkCorporates(corporatePage + 1, corporateSearch, true) }}
                  notFoundContent={corporateLoading ? <Spin size="small" /> : 'No corporates found'}
                />
              </div>

              {/* Salary Range */}
              {/* <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Salary (CTC) &nbsp;<span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af', fontSize: 10 }}>
                    {filters.salary_min !== '' && filters.salary_max !== ''
                      ? `₹${Number(filters.salary_min) >= 100000 ? (Number(filters.salary_min)/100000).toFixed(1).replace(/\.0$/,'')+'L' : Number(filters.salary_min) >= 1000 ? (Number(filters.salary_min)/1000).toFixed(1).replace(/\.0$/,'')+'K' : filters.salary_min} — ${filters.salary_max==='-1'?'above ₹5L':`₹${Number(filters.salary_max)>=100000?(Number(filters.salary_max)/100000).toFixed(1).replace(/\.0$/,'')+'L':Number(filters.salary_max)>=1000?(Number(filters.salary_max)/1000).toFixed(1).replace(/\.0$/,'')+'K':filters.salary_max}`}`
                      : '₹0 — above ₹5L'}
                  </span>
                </div>
                <Slider range min={0} max={500000} step={10000}
                  value={[filters.salary_min !== '' ? Number(filters.salary_min) : 0, filters.salary_max === '-1' ? 500000 : filters.salary_max !== '' ? Number(filters.salary_max) : 500000]}
                  onChange={([min, max]) => setFilters(p => ({ ...p, salary_min: min===0&&max===500000?'':String(min), salary_max: min===0&&max===500000?'':max===500000?'-1':String(max) }))}
                  tooltip={{ formatter: val => val>=500000?'above ₹5L':val>=100000?`₹${(val/100000).toFixed(1).replace(/\.0$/,'')}L`:val>=1000?`₹${(val/1000).toFixed(1).replace(/\.0$/,'')}K`:`₹${val}` }}
                />
              </div> */}

              {/* Profile Completion */}
              {/* <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Profile % &nbsp;<span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>{filters.profile[0]}–{filters.profile[1]}%</span>
                </div>
                <Slider range min={0} max={100} value={filters.profile}
                  onChange={val => setFilters(p => ({ ...p, profile: val }))} />
              </div> */}

              {/* Notice Period */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Notice Period</div>
                <Radio.Group value={filters.notice_period} onChange={e => setFilters(p => ({ ...p, notice_period: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <Radio value={null}>All</Radio>
                  <Radio value="currently_serving">Currently Serving</Radio>
                  <Radio value="0_15">0–15 days</Radio>
                  <Radio value="1_month">1 month</Radio>
                  <Radio value="2_months">2 months</Radio>
                  <Radio value="3_months">3 months</Radio>
                  <Radio value="more_3_months">More than 3 months</Radio>
                </Radio.Group>
              </div>

              {/* Gender */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Gender</div>
                <Radio.Group value={filters.gender} onChange={e => setFilters(p => ({ ...p, gender: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <Radio value={null}>All</Radio>
                  <Radio value="MALE">Male</Radio>
                  <Radio value="FEMALE">Female</Radio>
                  <Radio value="TRANSGENDER">Transgender</Radio>
                  <Radio value="OTHER">Other</Radio>
                </Radio.Group>
              </div>

              

              {/* Passed Out */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Passed Out</div>
                <Radio.Group value={filters.passed_out} onChange={e => setFilters(p => ({ ...p, passed_out: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <Radio value={null}>All</Radio>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </div>

              {/* Exp Candidate */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Exp Candidate</div>
                <Radio.Group value={filters.is_exp_candidate} onChange={e => setFilters(p => ({ ...p, is_exp_candidate: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <Radio value={null}>All</Radio>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </div>

            </div>

            {/* ── Range sliders — separate row ── */}
            <div style={{ marginTop: 8, paddingTop: 20 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '40px 32px',
                alignItems: 'start',
              }}>

                {/* Profile Completion — move it out of the grid above too, see note */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Profile % &nbsp;
                    <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>
                      {filters.profile[0]}–{filters.profile[1]}%
                    </span>
                  </div>
                  <Slider range min={0} max={100} value={filters.profile}
                    onChange={val => setFilters(p => ({ ...p, profile: val }))} />
                </div>

                {/* Salary Range */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Salary (CTC) &nbsp;
                    <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af', fontSize: 10 }}>
                      {filters.salary_min !== '' && filters.salary_max !== ''
                        ? `₹${Number(filters.salary_min) >= 100000 ? (Number(filters.salary_min)/100000).toFixed(1).replace(/\.0$/,'')+'L' : Number(filters.salary_min) >= 1000 ? (Number(filters.salary_min)/1000).toFixed(1).replace(/\.0$/,'')+'K' : filters.salary_min} — ${filters.salary_max==='-1'?'above ₹5L':`₹${Number(filters.salary_max)>=100000?(Number(filters.salary_max)/100000).toFixed(1).replace(/\.0$/,'')+'L':Number(filters.salary_max)>=1000?(Number(filters.salary_max)/1000).toFixed(1).replace(/\.0$/,'')+'K':filters.salary_max}`}`
                        : '₹0 — above ₹5L'}
                    </span>
                  </div>
                  <Slider range min={0} max={500000} step={10000}
                    value={[filters.salary_min !== '' ? Number(filters.salary_min) : 0, filters.salary_max === '-1' ? 500000 : filters.salary_max !== '' ? Number(filters.salary_max) : 500000]}
                    onChange={([min, max]) => setFilters(p => ({ ...p, salary_min: min===0&&max===500000?'':String(min), salary_max: min===0&&max===500000?'':max===500000?'-1':String(max) }))}
                    tooltip={{ formatter: val => val>=500000?'above ₹5L':val>=100000?`₹${(val/100000).toFixed(1).replace(/\.0$/,'')}L`:val>=1000?`₹${(val/1000).toFixed(1).replace(/\.0$/,'')}K`:`₹${val}` }}
                  />
                </div>

              </div>
            </div>

            {/* Footer: Reset + Apply */}
            {/* <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0',
            }}>
              <Button.Secondary height="32px" text="Reset" onClick={onResetFilters} />
              <Button.Primary height="32px" text="Apply" onClick={() => setFilterOpen(false)} />
            </div> */}
          </div>
        )}
      </div>

      <NavWrapper column>
        {/* Search + Filter trigger */}
        <TableSearchFilter
          placeholder="Search by name / email / mobile"
          menu={<div />}
          open={isMenuOpen}
          // setOpen={(open) => { if (open) setFilterDrawer(true); setIsMenuOpen(false) }}
          setOpen={(open) => { if (open) setFilterOpen(prev => !prev); setIsMenuOpen(false) }}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setCurrentPage={setPage}
          defaultFirstPageNumber={0}
          currentCount={PAGE_SIZE * page + (data?.length || 0)}
          totalCount={total || 0}
          // onOpenChange={(open) => { if (open) setFilterDrawer(true); setIsMenuOpen(false) }}
          onOpenChange={(open) => { if (open) setFilterOpen(prev => !prev); setIsMenuOpen(false) }}
          withoutFilter={false}
          hideFilter={true}
          filterCount={activeFilterCount}
          onClearFilters={onClearFiltersOnly}
          clearInput={clearInput}
        />

        {loading ? (
          <EmptyWrapper center><Spin /></EmptyWrapper>
        ) : data.length === 0 ? (
          <EmptyWrapper center><Empty description="No students found!" /></EmptyWrapper>
        ) : (
          <>
            <StyledTable
              columns={columns}
              dataSource={data}
              rowKey={(r) => String(r?.id ?? Math.random())}
              pagination={false}
            />
            {total > 0 && (
              <Pagination
                onChange={(num) => setPage(num - 1)}
                totalPages={total}
                pageSize={PAGE_SIZE}
                current={page + 1}
              />
            )}
          </>
        )}
      </NavWrapper>


      {/* ─── Filter Drawer ────────────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Filters</span>
            <Badge count={activeFilterCount} size="small" />
          </div>
        }
        open={filterDrawer}
        placement="right"
        width={500}
        onClose={() => setFilterDrawer(false)}
        bodyStyle={{ padding: 0, display: 'flex', height: '100%' }}
      >
        {/* ── Left tab strip ── */}
        <div style={{
          width: 140, background: '#f9fafb', borderRight: '1px solid #e5e7eb',
          display: 'flex', flexDirection: 'column', paddingTop: 8, flexShrink: 0,
        }}>
          {[
            { key: 'basic', label: 'Basic' },
            { key: 'education', label: 'Education' },
            { key: 'roles', label: 'Roles' },
            { key: 'work_exp', label: 'Experience' },
          ].map(tab => (
            <div
              key={tab.key}
              onClick={() => {
                setActiveFilterTab(tab.key)
                if (tab.key === 'basic') {
                  if (cityOptions.length === 0) fetchCities(1, '')
                  if (stateOptions.length === 0) fetchStates(1, '')
                }
                if (tab.key === 'education') {
                  if (educationLevelOptions.length === 0) fetchEducationLevels()
                  if (campusOptions.length === 0) fetchCampuses(1, '')
                  if (degreeOptions.length === 0) fetchDegrees(1, '')
                  if (deptOptions.length === 0) fetchDepts(1, '')
                  if (passingYearOptions.length === 0) fetchPassingYears()
                }
                if (tab.key === 'roles') {
                  if (roleOptions.length === 0) fetchRoles(1, '')
                }
                if (tab.key === 'work_exp') {
                  if (companyOptions.length === 0) fetchWorkCompanies(1, '')
                  if (designationOptions.length === 0) fetchWorkDesignations(1, '')
                  if (industryOptions.length === 0) fetchWorkIndustries(1, '')
                  if (corporateOptions.length === 0) fetchWorkCorporates(1, '')
                }
              }}
              style={{
                padding: '12px 14px', fontSize: 13, cursor: 'pointer',
                fontWeight: activeFilterTab === tab.key ? 600 : 400,
                color: activeFilterTab === tab.key ? '#3249D7' : '#4b5563',
                background: activeFilterTab === tab.key ? '#fff' : 'transparent',
                borderRight: activeFilterTab === tab.key ? '3px solid #3249D7' : '3px solid transparent',
                borderRadius: '4px 0 0 4px',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {/* ── Right filter panel ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* BASIC tab */}
            {activeFilterTab === 'basic' && <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Gender</div>
                <Radio.Group
                  value={filters.gender}
                  onChange={e => setFilters(p => ({ ...p, gender: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                >
                  <Radio value={null}>All</Radio>
                  <Radio value="MALE">Male</Radio>
                  <Radio value="FEMALE">Female</Radio>
                  <Radio value="TRANSGENDER">Transgender</Radio>
                  <Radio value="OTHER">Other</Radio>
                </Radio.Group>
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Status</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select status"
                  allowClear
                  value={filters.status}
                  onChange={val => setFilters(p => ({ ...p, status: val ?? null }))}
                  options={[
                    { value: 2, label: 'Active' },
                    { value: -2, label: 'Inactive' },
                    { value: 1, label: 'Pending' },
                    { value: -1, label: 'Blacklisted' },
                    { value: -3, label: 'Not Interested' },
                    { value: 0, label: 'Unknown' },
                  ]}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Passed Out</div>
                <Radio.Group
                  value={filters.passed_out}
                  onChange={e => setFilters(p => ({ ...p, passed_out: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                >
                  <Radio value={null}>All</Radio>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Exp Candidate</div>
                <Radio.Group
                  value={filters.is_exp_candidate}
                  onChange={e => setFilters(p => ({ ...p, is_exp_candidate: e.target.value }))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                >
                  <Radio value={null}>All</Radio>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                  Profile Completion %
                  <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af', marginLeft: 8 }}>
                    {filters.profile[0]}% — {filters.profile[1]}%
                  </span>
                </div>
                <Slider
                  range min={0} max={100}
                  value={filters.profile}
                  onChange={val => setFilters(p => ({ ...p, profile: val }))}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Current City</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search city (3+ chars)..."
                  allowClear
                  value={filters.city}
                  onChange={val => {
                    const next = val ?? []
                    setFilters(p => ({ ...p, city: next }))
                    setCityOptions(prev => {
                      const selectedSet = new Set(next)
                      const selectedItems = next.map(v => ({ value: v, label: v }))
                      const rest = prev.filter(o => !selectedSet.has(o.value))
                      return [...selectedItems, ...rest]
                    })
                  }}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setCitySearch(''); setCityPage(1); setCityHasMore(true)
                      fetchCities(1, '').then(() => {
                        setCityOptions(prev => {
                          const selectedSet = new Set(filters.city)
                          const selectedItems = filters.city.map(v => ({ value: v, label: v }))
                          const rest = prev.filter(o => !selectedSet.has(o.value))
                          return [...selectedItems, ...rest]
                        })
                      })
                    }
                  }}
                  options={cityOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={cityLoading}
                  onSearch={val => {
                    clearTimeout(citySearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 3) return
                    citySearchTimer.current = setTimeout(() => { setCitySearch(val); fetchCities(1, val) }, 400)
                  }}
                  onClear={() => { setFilters(p => ({ ...p, city: [] })); setCitySearch(''); setCityOptions([]); setCityPage(1); setCityHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && cityHasMore && !cityLoading) fetchCities(cityPage + 1, citySearch, true)
                  }}
                  notFoundContent={cityLoading ? <Spin size="small" /> : 'No cities found'}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Current State</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search state (3+ chars)..."
                  allowClear
                  value={filters.state}
                  onChange={val => {
                    const next = val ?? []
                    setFilters(p => ({ ...p, state: next }))
                    setStateOptions(prev => {
                      const selectedSet = new Set(next)
                      const selectedItems = next.map(v => ({ value: v, label: v }))
                      const rest = prev.filter(o => !selectedSet.has(o.value))
                      return [...selectedItems, ...rest]
                    })
                  }}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setStateSearch(''); setStatePage(1); setStateHasMore(true)
                      fetchStates(1, '').then(() => {
                        setStateOptions(prev => {
                          const selectedSet = new Set(filters.state)
                          const selectedItems = filters.state.map(v => ({ value: v, label: v }))
                          const rest = prev.filter(o => !selectedSet.has(o.value))
                          return [...selectedItems, ...rest]
                        })
                      })
                    }
                  }}
                  options={stateOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={stateLoading}
                  onSearch={val => {
                    clearTimeout(stateSearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 3) return
                    stateSearchTimer.current = setTimeout(() => { setStateSearch(val); fetchStates(1, val) }, 400)
                  }}
                  onClear={() => { setFilters(p => ({ ...p, state: [] })); setStateSearch(''); setStateOptions([]); setStatePage(1); setStateHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && stateHasMore && !stateLoading) fetchStates(statePage + 1, stateSearch, true)
                  }}
                  notFoundContent={stateLoading ? <Spin size="small" /> : 'No states found'}
                />
              </div>
            </>}

            {/* EDUCATION tab */}
            {activeFilterTab === 'education' && <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Education Level</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select level"
                  allowClear
                  value={filters.education_level}
                  onChange={val => setFilters(p => ({ ...p, education_level: val ?? null }))}
                  options={educationLevelOptions}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Institute Campus</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search institute (3+ chars)..."
                  allowClear
                  value={filters.institute_campus_ids}
                  onChange={(val, opts) => {
                    const next = val ?? []
                      ; (opts || []).forEach(o => { if (o.label) campusLabelMapRef.current[o.value] = o.label })
                    setFilters(p => ({ ...p, institute_campus_ids: next }))
                    setCampusOptions(prev => {
                      const selectedSet = new Set(next)
                      const selectedItems = next.map(v => {
                        const found = prev.find(o => o.value === v) || (opts || []).find(o => o.value === v)
                        return found || { value: v, label: campusLabelMapRef.current[v] || String(v) }
                      })
                      const rest = prev.filter(o => !selectedSet.has(o.value))
                      return [...selectedItems, ...rest]
                    })
                  }}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setCampusSearch(''); setCampusPage(1); setCampusHasMore(true)
                      fetchCampuses(1, '').then(() => {
                        setCampusOptions(prev => {
                          prev.forEach(o => { if (o.label) campusLabelMapRef.current[o.value] = o.label })
                          const selectedIds = filters.institute_campus_ids
                          const selectedSet = new Set(selectedIds)
                          const selectedItems = selectedIds.map(v => {
                            const found = prev.find(o => o.value === v)
                            return found || { value: v, label: campusLabelMapRef.current[v] || String(v) }
                          })
                          const rest = prev.filter(o => !selectedSet.has(o.value))
                          return [...selectedItems, ...rest]
                        })
                      })
                    }
                  }}
                  options={campusOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={campusLoading}
                  onSearch={val => {
                    clearTimeout(campusSearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 3) return
                    campusSearchTimer.current = setTimeout(() => { setCampusSearch(val); fetchCampuses(1, val) }, 400)
                  }}
                  onClear={() => { setFilters(p => ({ ...p, institute_campus_ids: [] })); setCampusSearch(''); setCampusOptions([]); setCampusPage(1); setCampusHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && campusHasMore && !campusLoading) fetchCampuses(campusPage + 1, campusSearch, true)
                  }}
                  notFoundContent={campusLoading ? <Spin size="small" /> : 'No institutes found'}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Degree</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search degree (2+ chars)..."
                  allowClear
                  value={filters.degree}
                  onChange={val => setFilters(p => ({ ...p, degree: val ?? [] }))}
                  options={degreeOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={degreeLoading}
                  onSearch={val => {
                    clearTimeout(degreeSearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 2) return
                    degreeSearchTimer.current = setTimeout(() => { setDegreeSearch(val); fetchDegrees(1, val) }, 400)
                  }}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setDegreeSearch(''); setDegreePage(1); setDegreeHasMore(true)
                      fetchDegrees(1, '')
                    }
                  }}
                  onClear={() => { setFilters(p => ({ ...p, degree: [] })); setDegreeSearch(''); setDegreePage(1); setDegreeHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && degreeHasMore && !degreeLoading) fetchDegrees(degreePage + 1, degreeSearch, true)
                  }}
                  notFoundContent={degreeLoading ? <Spin size="small" /> : 'No degrees found'}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Department</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search department (2+ chars)..."
                  allowClear
                  value={filters.department}
                  onChange={val => setFilters(p => ({ ...p, department: val ?? [] }))}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setDeptSearch(''); setDeptPage(1); setDeptHasMore(true)
                      fetchDepts(1, '')
                    }
                  }}
                  options={deptOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={deptLoading}
                  onSearch={val => {
                    clearTimeout(deptSearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 2) return
                    deptSearchTimer.current = setTimeout(() => { setDeptSearch(val); fetchDepts(1, val) }, 400)
                  }}
                  onClear={() => { setFilters(p => ({ ...p, department: [] })); setDeptSearch(''); setDeptPage(1); setDeptHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && deptHasMore && !deptLoading) fetchDepts(deptPage + 1, deptSearch, true)
                  }}
                  notFoundContent={deptLoading ? <Spin size="small" /> : 'No departments found'}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                  Year of Passing
                </div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select year(s)..."
                  allowClear
                  value={filters.passing_years}
                  onChange={val => setFilters(p => ({ ...p, passing_years: val ?? [] }))}
                  maxTagCount="responsive"
                  loading={passingYearsLoading}
                  options={passingYearOptions}
                  notFoundContent={passingYearsLoading ? <Spin size="small" /> : 'No years found'}
                />
              </div>
            </>}

            {/* LOCATION tab */}
            {activeFilterTab === 'location' && <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Current City</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Search city (3+ chars)..."
                  allowClear
                  value={filters.city}
                  onChange={val => { const next = val ?? []; setFilters(p => ({ ...p, city: next })); setCityOptions(next.map(v => ({ value: v, label: v }))) }}
                  options={cityOptions}
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  loading={cityLoading}
                  onSearch={val => {
                    clearTimeout(citySearchTimer.current)
                    if (val.length === 0) return
                    if (val.length < 3) return
                    citySearchTimer.current = setTimeout(() => { setCitySearch(val); fetchCities(1, val) }, 400)
                  }}
                  onDropdownVisibleChange={open => { if (open) { setCitySearch(''); setCityPage(1); setCityHasMore(true); fetchCities(1, '') } }}
                  onClear={() => { setFilters(p => ({ ...p, city: [] })); setCitySearch(''); setCityOptions([]); setCityPage(1); setCityHasMore(true) }}
                  onPopupScroll={e => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target
                    if (scrollHeight - scrollTop <= clientHeight + 20 && cityHasMore && !cityLoading) fetchCities(cityPage + 1, citySearch, true)
                  }}
                  notFoundContent={cityLoading ? <Spin size="small" /> : 'No cities found'}
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                  Year of Passing
                </div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select year(s)..."
                  allowClear
                  value={filters.passing_years}
                  onChange={val => setFilters(p => ({ ...p, passing_years: val ?? [] }))}
                  maxTagCount="responsive"
                  loading={passingYearsLoading}
                  options={passingYearOptions}
                  notFoundContent={passingYearsLoading ? <Spin size="small" /> : 'No years found'}
                />
              </div>

            </>}

            {/* ROLES tab */}
            {activeFilterTab === 'roles' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Smart Role Search — type a role, get all matching candidates (no chip-picking) */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Role Search
                    <span style={{ textTransform: 'none', fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>— type a role, get all matching candidates</span>
                  </div>
                  <Input.Search
                    key={`rolesearch-tab-${filters.roleSearch}`}
                    allowClear
                    enterButton
                    placeholder="e.g. full stack, software developer"
                    defaultValue={filters.roleSearch}
                    onSearch={val => setFilters(p => ({ ...p, roleSearch: (val || '').trim() }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Roles Applied
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select roles..."
                    allowClear
                    value={filters.roles}
                    onChange={val => {
                      const next = val ?? []
                      setFilters(p => ({ ...p, roles: next }))
                      setRoleOptions(prev => {
                        const selectedSet = new Set(next)
                        const selectedItems = next.map(v => ({ value: v, label: v }))
                        const rest = prev.filter(o => !selectedSet.has(o.value))
                        return [...selectedItems, ...rest]
                      })
                    }}
                    onDropdownVisibleChange={open => {
                      if (open) {
                        setRolesSearch(''); setRolesPage(1); setRolesHasMore(true)
                        fetchRoles(1, '').then(() => {
                          setRoleOptions(prev => {
                            const selectedSet = new Set(filters.roles)
                            const selectedItems = filters.roles.map(v => ({ value: v, label: v }))
                            const rest = prev.filter(o => !selectedSet.has(o.value))
                            return [...selectedItems, ...rest]
                          })
                        })
                      }
                    }}
                    options={roleOptions}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={false}
                    loading={rolesLoading}
                    onSearch={val => {
                      clearTimeout(rolesSearchTimer.current)
                      if (val.length === 0) return
                      if (val.length < 3) return
                      rolesSearchTimer.current = setTimeout(() => { setRolesSearch(val); fetchRoles(1, val) }, 400)
                    }}
                    onClear={() => { setFilters(p => ({ ...p, roles: [] })); setRolesSearch(''); setRoleOptions([]); setRolesPage(1); setRolesHasMore(true) }}
                    onPopupScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target
                      if (scrollHeight - scrollTop <= clientHeight + 20 && rolesHasMore && !rolesLoading) {
                        fetchRoles(rolesPage + 1, rolesSearch, true)
                      }
                    }}
                    notFoundContent={rolesLoading ? <Spin size="small" /> : 'No roles found'}
                  />
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                    Salary Range (CTC)
                    <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af', marginLeft: 8 }}>
                      {filters.salary_min !== '' && filters.salary_max !== ''
                        ? `₹${Number(filters.salary_min) >= 100000
                          ? (Number(filters.salary_min) / 100000).toFixed(1).replace(/\.0$/, '') + 'L'
                          : Number(filters.salary_min) >= 1000
                            ? (Number(filters.salary_min) / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
                            : filters.salary_min
                        } — ${filters.salary_max === '-1'
                          ? 'above ₹5L'
                          : `₹${Number(filters.salary_max) >= 100000
                            ? (Number(filters.salary_max) / 100000).toFixed(1).replace(/\.0$/, '') + 'L'
                            : Number(filters.salary_max) >= 1000
                              ? (Number(filters.salary_max) / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
                              : filters.salary_max
                          }`
                        }`
                        : '₹0 — above ₹5L'
                      }
                    </span>
                  </div>
                  <Slider
                    range
                    min={0}
                    max={500000}
                    step={10000}
                    value={[
                      filters.salary_min !== '' ? Number(filters.salary_min) : 0,
                      filters.salary_max === '-1' ? 500000 : filters.salary_max !== '' ? Number(filters.salary_max) : 500000,
                    ]}
                    onChange={([min, max]) =>
                      setFilters(p => ({
                        ...p,
                        salary_min: min === 0 && max === 500000 ? '' : String(min),
                        salary_max: min === 0 && max === 500000 ? '' : max === 500000 ? '-1' : String(max),
                      }))
                    }
                    tooltip={{
                      formatter: (val) => {
                        if (val >= 500000) return `above ₹5L`
                        if (val >= 100000) return `₹${(val / 100000).toFixed(1).replace(/\.0$/, '')}L`
                        if (val >= 1000) return `₹${(val / 1000).toFixed(1).replace(/\.0$/, '')}K`
                        return `₹${val}`
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* WORK & LOCATION tab */}
            {activeFilterTab === 'work_exp' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Company
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select company..."
                    allowClear
                    value={filters.work_company}
                    onChange={val => {
                      const next = val ?? []
                      setFilters(p => ({ ...p, work_company: next }))
                      setCompanyOptions(prev => {
                        const selectedSet = new Set(next)
                        const selectedItems = next.map(v => ({ value: v, label: v }))
                        const rest = prev.filter(o => !selectedSet.has(o.value))
                        return [...selectedItems, ...rest]
                      })
                    }}
                    onDropdownVisibleChange={open => {
                      if (open) {
                        setCompanySearch(''); setCompanyPage(1); setCompanyHasMore(true)
                        fetchWorkCompanies(1, '').then(() => {
                          setCompanyOptions(prev => {
                            const selectedSet = new Set(filters.work_company)
                            const selectedItems = filters.work_company.map(v => ({ value: v, label: v }))
                            const rest = prev.filter(o => !selectedSet.has(o.value))
                            return [...selectedItems, ...rest]
                          })
                        })
                      }
                    }}
                    options={companyOptions}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={false}
                    loading={companyLoading}
                    onSearch={val => {
                      clearTimeout(companySearchTimer.current)
                      if (val.length === 0) return
                      if (val.length < 3) return
                      companySearchTimer.current = setTimeout(() => { setCompanySearch(val); fetchWorkCompanies(1, val) }, 400)
                    }}
                    onClear={() => { setFilters(p => ({ ...p, work_company: [] })); setCompanySearch(''); setCompanyOptions([]); setCompanyPage(1); setCompanyHasMore(true) }}
                    onPopupScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target
                      if (scrollHeight - scrollTop <= clientHeight + 20 && companyHasMore && !companyLoading) {
                        fetchWorkCompanies(companyPage + 1, companySearch, true)
                      }
                    }}
                    notFoundContent={companyLoading ? <Spin size="small" /> : 'No companies found'}
                  />
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Designation
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select designation..."
                    allowClear
                    value={filters.work_designation}
                    onChange={val => {
                      const next = val ?? []
                      setFilters(p => ({ ...p, work_designation: next }))
                      setDesignationOptions(prev => {
                        const selectedSet = new Set(next)
                        const selectedItems = next.map(v => ({ value: v, label: v }))
                        const rest = prev.filter(o => !selectedSet.has(o.value))
                        return [...selectedItems, ...rest]
                      })
                    }}
                    onDropdownVisibleChange={open => {
                      if (open) {
                        setDesignationSearch(''); setDesignationPage(1); setDesignationHasMore(true)
                        fetchWorkDesignations(1, '').then(() => {
                          setDesignationOptions(prev => {
                            const selectedSet = new Set(filters.work_designation)
                            const selectedItems = filters.work_designation.map(v => ({ value: v, label: v }))
                            const rest = prev.filter(o => !selectedSet.has(o.value))
                            return [...selectedItems, ...rest]
                          })
                        })
                      }
                    }}
                    options={designationOptions}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={false}
                    loading={designationLoading}
                    onSearch={val => {
                      clearTimeout(designationSearchTimer.current)
                      if (val.length === 0) return
                      if (val.length < 3) return
                      designationSearchTimer.current = setTimeout(() => { setDesignationSearch(val); fetchWorkDesignations(1, val) }, 400)
                    }}
                    onClear={() => { setFilters(p => ({ ...p, work_designation: [] })); setDesignationSearch(''); setDesignationOptions([]); setDesignationPage(1); setDesignationHasMore(true) }}
                    onPopupScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target
                      if (scrollHeight - scrollTop <= clientHeight + 20 && designationHasMore && !designationLoading) {
                        fetchWorkDesignations(designationPage + 1, designationSearch, true)
                      }
                    }}
                    notFoundContent={designationLoading ? <Spin size="small" /> : 'No designations found'}
                  />
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Industry
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select industry..."
                    allowClear
                    value={filters.work_industry}
                    onChange={val => {
                      const next = val ?? []
                      setFilters(p => ({ ...p, work_industry: next }))
                      setIndustryOptions(prev => {
                        const selectedSet = new Set(next)
                        const selectedItems = next.map(v => ({ value: v, label: v }))
                        const rest = prev.filter(o => !selectedSet.has(o.value))
                        return [...selectedItems, ...rest]
                      })
                    }}
                    onDropdownVisibleChange={open => {
                      if (open) {
                        setIndustrySearch(''); setIndustryPage(1); setIndustryHasMore(true)
                        fetchWorkIndustries(1, '').then(() => {
                          setIndustryOptions(prev => {
                            const selectedSet = new Set(filters.work_industry)
                            const selectedItems = filters.work_industry.map(v => ({ value: v, label: v }))
                            const rest = prev.filter(o => !selectedSet.has(o.value))
                            return [...selectedItems, ...rest]
                          })
                        })
                      }
                    }}
                    options={industryOptions}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={false}
                    loading={industryLoading}
                    onSearch={val => {
                      clearTimeout(industrySearchTimer.current)
                      if (val.length === 0) return
                      if (val.length < 3) return
                      industrySearchTimer.current = setTimeout(() => { setIndustrySearch(val); fetchWorkIndustries(1, val) }, 400)
                    }}
                    onClear={() => { setFilters(p => ({ ...p, work_industry: [] })); setIndustrySearch(''); setIndustryOptions([]); setIndustryPage(1); setIndustryHasMore(true) }}
                    onPopupScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target
                      if (scrollHeight - scrollTop <= clientHeight + 20 && industryHasMore && !industryLoading) {
                        fetchWorkIndustries(industryPage + 1, industrySearch, true)
                      }
                    }}
                    notFoundContent={industryLoading ? <Spin size="small" /> : 'No industries found'}
                  />
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Work Location
                  </div>
                  <Input
                    placeholder="e.g. Chennai"
                    value={filters.work_location}
                    onChange={e => setFilters(p => ({ ...p, work_location: e.target.value }))}
                  />
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Job Application Date
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <DatePicker
                      placeholder="From"
                      style={{ flex: 1 }}
                      value={filters.work_started_from ? moment(filters.work_started_from, 'YYYY-MM-DD') : null}
                      onChange={(date) => setFilters(p => ({ ...p, work_started_from: date ? date.format('YYYY-MM-DD') : null }))}
                      format="DD/MM/YYYY"
                    />
                    <DatePicker placeholder="To" style={{ flex: 1 }}
                    value={filters.work_started_to ? moment(filters.work_started_to, 'YYYY-MM-DD') : null}
                    onChange={(date) => setFilters(p => ({ ...p, work_started_to: date ? date.format('YYYY-MM-DD') : null }))}
                    disabledDate={(current) => filters.work_started_from ? current && current < moment(filters.work_started_from, 'YYYY-MM-DD') : false}
                    format="DD/MM/YYYY" />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Notice Period
                  </div>
                  <Radio.Group
                    value={filters.notice_period}
                    onChange={e => setFilters(p => ({ ...p, notice_period: e.target.value }))}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                  >
                    <Radio value={null}>All</Radio>
                    <Radio value="currently_serving">Currently Serving (0 days)</Radio>
                    <Radio value="0_15">0–15 days</Radio>
                    <Radio value="1_month">1 month (16–30 days)</Radio>
                    <Radio value="2_months">2 months (31–60 days)</Radio>
                    <Radio value="3_months">3 months (61–90 days)</Radio>
                    <Radio value="more_3_months">More than 3 months</Radio>
                  </Radio.Group>
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Corporate Entity
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select corporate..."
                    allowClear
                    value={filters.work_corporate_ids}
                    onChange={(val, opts) => {
                      const next = val ?? []
                      setFilters(p => ({ ...p, work_corporate_ids: next }))
                      setCorporateOptions(prev => {
                        const selectedSet = new Set(next)
                        const selectedItems = next.map(v => {
                          const found = prev.find(o => o.value === v) || (opts || []).find(o => o.value === v)
                          return found || { value: v, label: String(v) }
                        })
                        const rest = prev.filter(o => !selectedSet.has(o.value))
                        return [...selectedItems, ...rest]
                      })
                    }}
                    onDropdownVisibleChange={open => {
                      if (open) {
                        setCorporateSearch(''); setCorporatePage(1); setCorporateHasMore(true)
                        fetchWorkCorporates(1, '').then(() => {
                          setCorporateOptions(prev => {
                            const selectedIds = filters.work_corporate_ids
                            const selectedSet = new Set(selectedIds)
                            const selectedItems = selectedIds.map(v => {
                              const found = prev.find(o => o.value === v)
                              return found || { value: v, label: String(v) }
                            })
                            const rest = prev.filter(o => !selectedSet.has(o.value))
                            return [...selectedItems, ...rest]
                          })
                        })
                      }
                    }}
                    options={corporateOptions}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={false}
                    loading={corporateLoading}
                    onSearch={val => {
                      clearTimeout(corporateSearchTimer.current)
                      if (val.length === 0) return
                      if (val.length < 3) return
                      corporateSearchTimer.current = setTimeout(() => { setCorporateSearch(val); fetchWorkCorporates(1, val) }, 400)
                    }}
                    onClear={() => { setFilters(p => ({ ...p, work_corporate_ids: [] })); setCorporateSearch(''); setCorporateOptions([]); setCorporatePage(1); setCorporateHasMore(true) }}
                    onPopupScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target
                      if (scrollHeight - scrollTop <= clientHeight + 20 && corporateHasMore && !corporateLoading) {
                        fetchWorkCorporates(corporatePage + 1, corporateSearch, true)
                      }
                    }}
                    notFoundContent={corporateLoading ? <Spin size="small" /> : 'No corporates found'}
                  />
                </div>

                {/* <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                    Salary Range (CTC)
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Input
                      type="number"
                      placeholder="Min (e.g. 10000)"
                      value={filters.salary_min}
                      min={0}
                      onChange={e => setFilters(p => ({ ...p, salary_min: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: 13, flexShrink: 0 }}>to</span>
                    <Input
                      type="number"
                      placeholder="Max (e.g. 50000)"
                      value={filters.salary_max}
                      min={0}
                      onChange={e => setFilters(p => ({ ...p, salary_max: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </div>
                  {filters.salary_min !== '' && filters.salary_max !== '' && (
                    <div style={{ fontSize: 11, color: '#3249D7', marginTop: 6 }}>
                      Filtering roles where min = {filters.salary_min} and max = {filters.salary_max}
                    </div>
                  )}
                  {(filters.salary_min !== '') !== (filters.salary_max !== '') && (
                    <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>
                      Please enter both min and max for salary filter
                    </div>
                  )}
                </div> */}

              </div>
            )}

          </div>

          {/* ── Sticky footer ── */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #eef0f4',
            background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 10,
          }}>
            <Button.Secondary height="32px" text="Reset" onClick={onResetFilters} />
            <Button.Primary height="32px" text="Apply" onClick={() => setFilterDrawer(false)} />
          </div>
        </div>
      </Drawer>


      {/* ─── Download Modal ───────────────────────────────────────────────── */}
      <Modal
        open={downloadModal}
        onCancel={resetDownloadModal}
        width={850}
        title={`Download (${selectedCols.length} selected)`}
        bodyStyle={{ maxHeight: 550, overflow: 'hidden' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Format:</span>
                <Radio.Group
                  value={fileFormat}
                  onChange={e => setFileFormat(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="xlsx">XLSX</Radio.Button>
                  <Radio.Button value="csv">CSV</Radio.Button>
                </Radio.Group>
              </div>
              {currentUserEmail && (
                <span style={{ fontSize: 13, color: '#637487' }}>
                  We&apos;ll email it to <b>{currentUserEmail}</b> when it&apos;s ready
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button.Secondary text="Cancel" onClick={resetDownloadModal} />
              <Button.Primary
                text={downloadLoading ? 'Downloading...' : 'Download'}
                onClick={handleDownload}
                disabled={downloadLoading}
              />
            </div>
          </div>
        }
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <ColList
              droppableId="available"
              items={availableCols}
              checks={availableChecks}
              setChecks={setAvailableChecks}
            />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10, paddingTop: 40 }}>
              <AntButton
                onClick={moveCheckedToSelected}
                disabled={!Object.values(availableChecks).some(Boolean)}
                icon={<RightOutlined />}
                style={{ width: 40, height: 40, borderColor: '#3249D7', color: '#3249D7' }}
              />
              <AntButton
                onClick={moveCheckedToAvailable}
                disabled={!Object.values(selectedChecks).some(Boolean)}
                icon={<LeftOutlined />}
                style={{ width: 40, height: 40, borderColor: '#3249D7', color: '#3249D7' }}
              />
            </div>
            <ColList
              droppableId="selected"
              items={selectedCols}
              checks={selectedChecks}
              setChecks={setSelectedChecks}
            />
          </div>
        </DragDropContext>
      </Modal>

      {/* ─── Detail Drawer ────────────────────────────────────────────────── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {s?.admin?.firstName
                ? `${s.admin.firstName} ${s.admin.lastName || ''}`.trim()
                : 'Student Details'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
              {s?.admin?.email || ''}
            </div>
            {/* in drawer title, use selectedRow as fallback: */}
            {(s?.source_file || selectedRow?.source_file) && (
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
                    📄 {s?.source_file || selectedRow?.source_file}
                </div>
            )}
          </div>
        }
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSelectedStudent(null)
          setAssessmentData(null)
          setApiLogData(null)
        }}
        width={900}
        bodyStyle={{ padding: 16, background: '#fafbfc' }}
        destroyOnClose
      >
        {drawerLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
            <Spin size="large" />
          </div>
        ) : !s ? (
          <Empty description="No data available" />
        ) : (
          <Tabs
            defaultActiveKey="live_data_analysis"
            onChange={(key) => {
              if (key === 'normalized') {
                fetchNormalizedData(selectedStudentId)
              }
              // if (key === 'live_data_analysis' && !assessmentData) {
              //   fetchAssessmentData(selectedStudentId)
              // }
              if (key === 'payload_response') {
                if (!apiLogData) fetchApiLog(selectedStudentId)
              }

            }}
            items={[
              /* ───────── STRUCTURED TAB ───────── */
              {
                key: 'live_data_analysis',
                label: 'Summary',
                children: (
                  <div style={{ paddingTop: 8 }}>
                    {renderLiveDataAnalysis()}
                  </div>
                )
              },
              {
                key: 'structured',
                label: 'Live Data',
                children: (
                  <div>
                    <InfoSection
                      title="🎓 Student Info"
                      fields={STUDENT_FIELDS}
                      data={s.student}
                      column={1}
                    />

                    <InfoSection
                      title="👤 Personal Profile"
                      fields={PERSONAL_FIELDS}
                      data={s.studentPersonalProfile}
                      column={1}
                    />

                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 16
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                        📖 Current Course
                      </div>

                      {s.currentCourse ? (
                        <Descriptions bordered size="small" column={1}>
                          {CURRENT_COURSE_FIELDS.map(({ label, key, render }) => (
                            <Descriptions.Item key={key} label={label}>
                              {render
                                ? render(s.currentCourse)
                                : smartFormat(s.currentCourse?.[key])}
                            </Descriptions.Item>
                          ))}
                        </Descriptions>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>
                          No current course data
                        </span>
                      )}
                    </div>


                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 16
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                        📚 Education History ({s.education?.length || 0} records)
                      </div>

                      <EducationHistory education={s.education} />
                    </div>

                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 16
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                        📄 Resume
                      </div>

                      {s.resume?.objective ? (
                        <div
                          style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}
                          dangerouslySetInnerHTML={{
                            __html: s.resume.objective
                          }}
                        />
                      ) : (
                        <span style={{ color: '#9ca3af' }}>
                          No resume data
                        </span>
                      )}
                    </div>

                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 16
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                        💼 Roles Applied
                      </div>

                      {s.roles?.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {s.roles.map((r, i) => (
                            <Tag key={i} color="blue">{r}</Tag>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>
                          No roles found
                        </span>
                      )}
                    </div>

                    {/* <div style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                            💼 Work Experience
                        </div>

                        {!s.work_experience?.length ? (
                            <span style={{ color: '#9ca3af' }}>No work experience data</span>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {s.work_experience.map((w, i) => (
                                    <div key={w.id || i} style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 8,
                                        padding: 14,
                                        background: '#fafafa',
                                    }}>
                                        {[
                                            { label: 'Organization',   value: w.organization?.trim() },
                                            { label: 'Title',          value: w.title },
                                            { label: 'Role',           value: w.role },
                                            { label: 'Job Type',       value: w.jobType },
                                            { label: 'Work Location',  value: w.work_location },
                                            { label: 'Started',        value: w.started_in },
                                            { label: 'Ended',          value: w.isCurrentlyWorking ? 'Currently Working' : w.ended_in },
                                            { label: 'Skills',         value: Array.isArray(w.skill_set) && w.skill_set.length ? w.skill_set.map(s => s.name).filter(Boolean).join(', ') : null },
                                        ].map(({ label, value }) => (
                                            <div key={label} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '7px 0',
                                                borderBottom: '1px solid #f0f0f0',
                                                fontSize: 13,
                                            }}>
                                                <span style={{ color: '#6b7280', fontWeight: 500, minWidth: 120 }}>{label}</span>
                                                <span style={{ color: value ? '#111827' : '#9ca3af', textAlign: 'right', flex: 1 }}>
                                                    {value || 'Not Available'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div> */}

                  </div>
                )
              },

              /* ───────── NORMALIZED TAB ───────── */
              {
                key: 'normalized',
                label: 'Normalized Data',
                children: (
                  <div style={{ paddingTop: 8 }}>
                    {normalizedLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                        <Spin />
                      </div>
                    ) : (
                      <NormalizedGrid data={normalizedData} />
                    )}
                  </div>
                )
              },
              // /* ───────── Assessment View TAB ───────── */
              // {
              //   key: 'assessment',
              //   label: 'Assessment Score',
              //   children: (
              //     <div style={{ paddingTop: 8 }}>
              //       {assessmentLoading ? (
              //         <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              //           <Spin />
              //         </div>
              //       ) : (
              //         renderAssessmentScores()
              //       )}
              //     </div>
              //   )
              // },

              /* ───────── CV Data TAB ───────── */
              {
                key: 'cv_data',
                label: 'CV Data',
                children: (
                  <div style={{ paddingTop: 8 }}>
                    {!s?.cv_data ? (
                      s?.cv_parse_error ? (
                        <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 8, padding: 16, color: '#b91c1c', fontSize: 13, wordBreak: 'break-word' }}>
                          <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠️ CV Parse Error</div>
                          {s.cv_parse_error}
                        </div>
                      ) : (
                        <Empty description="No CV data available" />
                      )
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {Object.entries(s.cv_data).map(([sectionKey, sectionVal]) => {

                          // ── CV URL ──
                          if (sectionKey === 'cvUrl') {
                            const cvObj = typeof sectionVal === 'object' ? sectionVal : { url: sectionVal }
                            const cvUrl = cvObj?.url
                            const hasCvUrl = cvUrl && typeof cvUrl === 'string' && cvUrl.trim() !== '' && cvUrl !== 'null'
                            return (
                              <div key={sectionKey} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                                  📎 CV / Resume
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {Object.entries(cvObj).map(([subKey, subVal]) => {
                                    const isEmpty = subVal === null || subVal === undefined || subVal === '' || subVal === 0
                                    const isUrl = subKey === 'url' && !isEmpty
                                    return (
                                      <div key={subKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f9fafb', fontSize: 13 }}>
                                        <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, minWidth: 60, flexShrink: 0 }}>
                                          {subKey}
                                        </span>
                                        {isUrl ? (
                                          <a
                                            href={subVal}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#3249D7', wordBreak: 'break-all', textAlign: 'right', flex: 1, marginLeft: 12, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}
                                          >
                                            <DownloadOutlined style={{ fontSize: 13, flexShrink: 0 }} />
                                            View / Download
                                          </a>
                                        ) : (
                                          <span style={{ color: isEmpty ? '#9ca3af' : '#111827', textAlign: 'right', flex: 1, marginLeft: 12, wordBreak: 'break-word' }}>
                                            {isEmpty ? 'N/A' : String(subVal)}
                                          </span>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          }

                          // ── Arrays (education, workExperience, projects, courses, etc.) ──
                          if (Array.isArray(sectionVal)) {
                            const filtered = sectionVal.filter(item =>
                              item && typeof item === 'object' && Object.values(item).some(v =>
                                v !== null && v !== undefined && v !== '' && v !== 0 &&
                                !(Array.isArray(v) && v.length === 0) &&
                                !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)
                              )
                            )
                            if (!filtered.length) return null

                            return (
                              <div key={sectionKey} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                                  {sectionKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()} ({filtered.length})
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  {filtered.map((item, i) => (
                                    <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 14px' }}>
                                      {Object.entries(item).map(([fieldKey, fieldVal]) => {

                                        if (typeof fieldVal === 'object' && !Array.isArray(fieldVal) && Object.keys(fieldVal).length === 0) return null

                                        const label = fieldKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()

                                        // skill_set or any array of objects with name
                                        if (Array.isArray(fieldVal)) {
                                          const names = fieldVal
                                            .map(s => (typeof s === 'object' ? s?.name : String(s)))
                                            .filter(Boolean)
                                          if (!names.length) return null
                                          return (
                                            <div key={fieldKey} style={{ marginTop: 6 }}>
                                              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>
                                                {label}
                                              </div>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                               {names.map((name, j) => (
                                                  <span key={j} style={{ fontSize: 11, padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 4, fontWeight: 500 }}>
                                                    {name}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )
                                        }

                                        // nested object (like certificate)
                                        if (typeof fieldVal === 'object') {
                                          const url = fieldVal?.url
                                          if (url && typeof url === 'string' && url.trim() !== '') {
                                            return (
                                              <div key={fieldKey} style={{ marginTop: 4, fontSize: 12 }}>
                                                <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: 11 }}>{label}: </span>
                                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#3249D7' }}>
                                                  {fieldVal?.name || 'View'}
                                                </a>
                                              </div>
                                            )
                                          }
                                          return null
                                        }

                                        // long text (description)
                                        if (typeof fieldVal === 'string' && fieldVal.length > 100) {
                                          return (
                                            <div key={fieldKey} style={{ marginTop: 8 }}>
                                              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                                                {label}
                                              </div>
                                              <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                                {fieldVal}
                                              </div>
                                            </div>
                                          )
                                        }

                                        const isEmpty = fieldVal === null || fieldVal === undefined || fieldVal === '' || fieldVal === 0
                                        return (
                                          <div key={fieldKey} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                                            <span style={{ color: '#6b7280', fontWeight: 500, minWidth: 120, textTransform: 'capitalize' }}>{label}</span>
                                            <span style={{ color: isEmpty ? '#9ca3af' : '#111827', textAlign: 'right', flex: 1 }}>
                                              {isEmpty ? 'N/A' : typeof fieldVal === 'boolean' ? (fieldVal ? 'Yes' : 'No') : String(fieldVal)}
                                            </span>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          }

                          // ── Flat objects (admin, student, studentPersonalProfile, etc.) ──
                          if (typeof sectionVal === 'object' && sectionVal !== null) {
                            const entries = Object.entries(sectionVal).filter(([, v]) =>
                              typeof v !== 'object' || v === null
                            )
                            const objectEntries = Object.entries(sectionVal).filter(([, v]) =>
                              v !== null && typeof v === 'object' && !Array.isArray(v)
                            )
                            if (!entries.length && !objectEntries.length) return null

                            return (
                              <div key={sectionKey} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                                  {sectionKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                                    {entries.map(([key, val]) => {
                                      const isEmpty = val === null || val === undefined || val === '' || val === 0
                                      return (
                                        <div key={key} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px' }}>
                                          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                                            {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                          </div>
                                          <div style={{ fontSize: 13, color: isEmpty ? '#9ca3af' : '#111827', fontWeight: 500, wordBreak: 'break-word' }}>
                                            {isEmpty ? 'N/A' : typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                                          </div>
                                        </div>
                                      )
                                    })}
                                    {objectEntries.map(([key, val]) => (
                                      <div key={key} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', gridColumn: 'span 1' }}>
                                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                          {Object.entries(val).map(([subKey, subVal]) => {
                                            const isEmpty = subVal === null || subVal === undefined || subVal === '' || subVal === 0
                                            const isUrl = subKey === 'url' && typeof subVal === 'string' && subVal.trim() !== ''
                                            return (
                                              <div key={subKey} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, minWidth: 50 }}>{subKey}</span>
                                                {isUrl ? (
                                                  <a href={subVal} target="_blank" rel="noopener noreferrer" style={{ color: '#3249D7', fontSize: 12, wordBreak: 'break-all', textAlign: 'right', flex: 1, marginLeft: 8 }}>
                                                    View
                                                  </a>
                                                ) : (
                                                  <span style={{ color: isEmpty ? '#9ca3af' : '#111827', textAlign: 'right', flex: 1, marginLeft: 8, wordBreak: 'break-word' }}>
                                                    {isEmpty ? 'N/A' : String(subVal)}
                                                  </span>
                                                )}
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                              </div>
                            )
                          }

                          // ── Primitive top-level keys ──
                          if (typeof sectionVal === 'string' || typeof sectionVal === 'number' || typeof sectionVal === 'boolean') {
                            if (!sectionVal && sectionVal !== false) return null
                            return (
                              <div key={sectionKey} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, textTransform: 'capitalize' }}>
                                  {sectionKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                </span>
                                <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>
                                  {typeof sectionVal === 'boolean' ? (sectionVal ? 'Yes' : 'No') : String(sectionVal)}
                                </span>
                              </div>
                            )
                          }

                          return null
                        })}
                      </div>
                    )}
                  </div>
                )
              },

              /* ───────── PAYLOAD AND RESPONSE TAB ───────── */
              {
                key: 'payload_response',
                label: 'Payload and Response',
                children: (
                  <div style={{ paddingTop: 8 }}>
                    {apiLogLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                        <Spin />
                      </div>
                    ) : !apiLogData || apiLogData.length === 0 ? (
                      <Empty description="No API log found" />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px' }}>
                        {apiLogData.map((entry, idx) => {
                          const key = `pr_${entry.role || idx}`
                          const isOpen = !!expandedRoles[key]
                          return (
                            <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                              <div
                                onClick={() => toggleRole(key)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', cursor: 'pointer', background: '#fff', userSelect: 'none' }}
                              >
                                <span style={{ fontSize: 11, color: '#6b7280', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}>▶</span>
                                <Tooltip title={entry.role || 'Unknown Role'}>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {entry.role || 'Unknown Role'}
                                  </span>
                                </Tooltip>
                                <Tag
                                  color={!entry.log_level ? 'default' : entry.log_level === 'INFO' ? 'green' : 'red'}
                                  style={{ margin: 0, fontWeight: 500, flexShrink: 0 }}
                                >
                                  {!entry.log_level ? 'No Log' : entry.log_level === 'INFO' ? 'Success' : 'Failed'}
                                </Tag>
                                {entry.created_at && (
                                  <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {new Date(entry.created_at).toLocaleString('en-IN')}
                                  </span>
                                )}
                              </div>
                              {isOpen && (
                                <div style={{ padding: '12px 20px 16px', background: '#fafbfc', borderTop: '1px solid #f0f0f0' }}>
                                  {!entry.log_level ? (
                                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>
                                      No API log found for this role
                                    </div>
                                  ) : (
                                    <>
                                      {entry.message && (
                                        <div style={{ fontSize: 13, color: entry.log_level === 'INFO' ? '#15803d' : '#dc2626', background: entry.log_level === 'INFO' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${entry.log_level === 'INFO' ? '#bbf7d0' : '#fecaca'}`, borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>
                                          {entry.message}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        <div>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Request Payload</div>
                                          {entry.request ? (
                                            <pre style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', fontSize: 12, lineHeight: 1.6, overflowX: 'auto', maxHeight: 350, overflowY: 'auto', margin: 0, color: '#1e293b' }}>
                                              {JSON.stringify(entry.request, null, 2)}
                                            </pre>
                                          ) : (
                                            <div style={{ textAlign: 'center', padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>No request payload available</div>
                                          )}
                                        </div>
                                        <div>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>API Response</div>
                                          {entry.response ? (
                                            <pre style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', fontSize: 12, lineHeight: 1.6, overflowX: 'auto', maxHeight: 350, overflowY: 'auto', margin: 0, color: '#1e293b' }}>
                                              {JSON.stringify(entry.response, null, 2)}
                                            </pre>
                                          ) : (
                                            <div style={{ textAlign: 'center', padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>No response data available</div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              },

              // {
              //   key: 'sheet_info',
              //   label: 'Sheet Info',
              //   children: (
              //     <div style={{ paddingTop: 8 }}>
              //       {!(s?.source_file || selectedRow?.source_file) ? (
              //         <Empty description="No sheet info available" />
              //       ) : (
              //         <div style={{
              //           background: '#fff',
              //           border: '1px solid #e5e7eb',
              //           borderRadius: 8,
              //           padding: 20,
              //           display: 'flex',
              //           flexDirection: 'column',
              //           gap: 16,
              //         }}>
              //           <div style={{
              //             fontSize: 13,
              //             fontWeight: 600,
              //             color: '#6b7280',
              //             textTransform: 'uppercase',
              //             letterSpacing: '0.05em',
              //             borderBottom: '1px solid #f0f0f0',
              //             paddingBottom: 10,
              //           }}>
              //             Source Details
              //           </div>

              //           {[
              //             { label: 'Source File', value: selectedRow?.source_file, icon: '📄' },
              //           ].map(({ label, value, icon }) => (
              //             value ? (
              //               <div key={label} style={{
              //                 display: 'flex',
              //                 alignItems: 'flex-start',
              //                 gap: 12,
              //                 padding: '12px 0',
              //                 borderBottom: '1px solid #f9fafb',
              //               }}>
              //                 <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
              //                 <div>
              //                   <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
              //                     {label}
              //                   </div>
              //                   <div style={{ fontSize: 13, color: '#111827', fontWeight: 500, wordBreak: 'break-all' }}>
              //                     {value}
              //                   </div>
              //                 </div>
              //               </div>
              //             ) : null
              //           ))}
              //         </div>
              //       )}
              //     </div>
              //   )
              // }

            ]}
          />
        )}
      </Drawer>
      {/* <ExportToast /> */}
    </SectionWrapper>
  )
}

export default CandidateMetricMain
